<template>
  <v-container class="chat-wrapper pa-0 d-flex flex-column">
    <!-- <v-responsive class="align-center text-center flex-grow-1 d-flex flex-column"> -->
    <v-card class="d-flex flex-column w-100 chat-card" flat>
      <v-card-title class="headline flex-shrink-0 d-flex align-center py-0">
        <slot name="agent-selector">
          <span class="chat-header-agent-name">{{ agent.name }}</span>
        </slot>
        <span v-if="session.title && session.title !== 'New Chat'" class="ml-1 text-body-2 chat-header-title">&nbsp;&nbsp;{{ session.title }}</span>
        <v-spacer />
        <span class="text-caption text-medium-emphasis ml-3 chat-header-description">{{ agent.description }}</span>
        <v-tooltip text="Share this agent" location="top" v-if="username === ownerUsername">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon="mdi-share-variant" size="small" variant="text" color="primary"
              @click="showShare = true" />
          </template>
        </v-tooltip>
        <AgentShareDialog v-model="showShare" :ownerUsername="ownerUsername" :slug="agentSlug"
          :agentName="agent.name" />
      </v-card-title>
      <v-divider class="flex-shrink-0"></v-divider>
      <v-card-text class="chat-area flex-grow-1" ref="chatArea">
        <!-- Chat Messages -->
        <v-row v-for="({ message, sourceIndex }, displayIndex) in displayMessages" :key="`${session.id}-${sourceIndex}`"
          :ref="(el: any) => { if (el) messageElements[displayIndex] = el as any }"
          :justify="message.role === 'user' ? 'end' : 'start'" no-gutters class="mb-4">
          <v-col cols="12" sm="10" md="9" lg="9">
            <!-- Add name label above message -->
            <div class="message-name text-caption mb-1" :class="message.role === 'user' ? 'text-right' : 'text-left'">
              {{ message.role === 'user' ? (username || 'You') : agent.name }}
            </div>


            <!-- Wrap FormattingCard with styled container -->
            <div class="message-container" :class="[
                message.role === 'user' ? 'user-message pb-6' : 'assistant-message'
              ]">
              <!-- Tool Events Panel: Shown for assistant messages with tool events -->
              <ToolEventsPanel
                v-if="message.role === 'assistant'"
                :tool-events="message.toolEvents || []"
                :show-activity="shouldShowToolActivity(message, sourceIndex)"
              />
              <FormattingCard :content="message.content" :expanded="true" :title="null"
                :streaming="streamingMessageIndex === sourceIndex" class="text-wrap text-break" />
              <!-- Refusal notice: the model ended this turn itself (safety classifiers). Rendered below
                   whatever content it did produce, as an in-conversation state rather than an error. -->
              <v-alert v-if="isRefusedMessage(message)" type="warning" variant="tonal" density="compact"
                icon="mdi-shield-alert-outline" class="refusal-notice mt-2">
                <div class="text-body-2 font-weight-medium">The model declined to continue this response.</div>
                <div class="text-caption">Rephrasing the request may help.</div>
              </v-alert>
              <!-- Per-response streaming audio player -->
              <StreamingAudioPlayer v-if="message.role === 'assistant' && agent.hasVoice"
                :ref="(el: any) => setStreamingPlayerRef(sourceIndex, el)" :auto-play="true"
                mime-type="audio/webm; codecs=opus" :canv-height="40" :fft-size="512" class="message-player" />
              <!-- Financial Charts Display -->
              <div v-if="hasFinancialChart(message)" class="mt-4">
                <v-expansion-panels variant="inset" density="compact" multiple>
                  <v-expansion-panel v-for="chartItem in getAllFinancialChartData(message)"
                    :key="`chart-${message.role}-${sourceIndex}-${chartItem.index}`">
                    <v-expansion-panel-title>
                      {{ getChartTitle(chartItem.data) }}
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="pa-0">
                      <CandlestickChart :chartData="chartItem.data" :width="getChartDimensions().width"
                        :height="getChartDimensions().height" :show-volume="true" :enable-zoom="true"
                        :show-tooltip="true" />
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </div>
              <!-- Token usage display -->
              <div v-if="message.role === 'assistant' && message.tokenUsage" class="token-usage-display mt-2">
                <span class="token-usage-label">Tokens: </span>
                <span class="token-usage-value">
                  Prompt: {{ message.tokenUsage.promptTokens }} |
                  Completion: {{ message.tokenUsage.completionTokens }} |
                  Total: {{ message.tokenUsage.totalTokens }}
                </span>
              </div>

              <!-- Message Copy Menu - Bottom Left -->
              <div class="message-copy-menu">
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-dots-horizontal" size="small" variant="text"
                      color="primary"></v-btn>
                  </template>
                  <v-list density="compact">
                    <v-list-item @click="copyMessageMarkdown(message)">
                      <v-list-item-title>Copy Markdown</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="copyMessageText(message)">
                      <v-list-item-title>Copy Text</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="message.toolEvents && message.toolEvents.length > 0"
                      @click="copyMessageToolEvents(message)">
                      <v-list-item-title>Copy Tool Events</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="copyMessageAll(message)">
                      <v-list-item-title>Copy All</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <!-- Message Email Button -->
              <div class="message-email-button">
                <v-tooltip text="Email Message" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-email-fast-outline" size="small" variant="text" color="primary"
                      @click="emailMessage(message)" :disabled="loading"></v-btn>
                  </template>
                </v-tooltip>
              </div>

              <!-- Message Download Button -->
              <div class="message-download-button" v-if="message.role === 'assistant' && agent.hasVoice">
                <v-tooltip text="Download Audio" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-download" size="small" variant="text" color="primary"
                      :disabled="loading" @click="downloadAudio(sourceIndex)"></v-btn>
                  </template>
                </v-tooltip>
              </div>

              <!-- Message Regenerate Button -->
              <div class="message-regenerate-button" v-if="message.role === 'assistant' && agent.hasVoice">
                <v-tooltip text="Regenerate Audio" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn v-bind="props" icon="mdi-reload" size="small" variant="text" color="primary"
                      :disabled="loading" @click="regenerateAudio(sourceIndex)"></v-btn>
                  </template>
                </v-tooltip>
              </div>

              <div class="message-retry-button" v-if="message.role === 'assistant' && shouldShowRetryButton(message, sourceIndex)">
                <v-tooltip text="Retry Response" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-refresh"
                      size="small"
                      variant="text"
                      color="primary"
                      :loading="message.retryState?.autoRetryPending === true"
                      :disabled="loading"
                      @click="retryFailedAssistantResponse(sourceIndex, 'manual')"
                    ></v-btn>
                  </template>
                </v-tooltip>
              </div>

              <div class="message-generating"
                v-if="message.role === 'assistant' && sourceIndex === streamingMessageIndex && loading">
                <v-progress-circular size="small" color="primary" indeterminate class="mr-2"></v-progress-circular>
              </div>
            </div>
          </v-col>
        </v-row>
        <!-- Full Chat Copy Menu - Bottom Right -->
        <div class="chat-copy-menu" v-if="localMessages.length > 0">
          <v-tooltip text="Email Conversation" location="top">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon="mdi-email-fast-outline" size="small" variant="text" color="primary"
                @click="emailAllMessages()" :disabled="loading" class="mr-2"></v-btn>
            </template>
          </v-tooltip>
          <v-menu location="top">
            <template v-slot:activator="{ props }">
              <v-btn v-bind="props" icon="mdi-dots-horizontal" size="small" variant="text" color="primary"></v-btn>
            </template>
            <v-list density="compact">
              <v-list-item @click="copyAllResponsesMarkdown()">
                <v-list-item-title>Copy All Responses as Markdown</v-list-item-title>
              </v-list-item>
              <v-list-item @click="copyAllResponsesWithToolEvents()">
                <v-list-item-title>Copy All Responses and Tool Events</v-list-item-title>
              </v-list-item>
              <v-list-item @click="copyAllMessagesWithToolEvents()">
                <v-list-item-title>Copy All Messages and Tool Events</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-card-text>
      <!-- Scroll-to-bottom FAB: shown when user scrolls up during streaming -->
      <v-btn v-if="showScrollToBottomBtn" class="scroll-to-bottom-fab" icon size="small" color="primary"
        elevation="4" @click="resumeAutoScroll">
        <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
      <div class="chat-input-area flex-shrink-0" ref="chatInputArea">
        <v-divider></v-divider>
        <!-- Image Upload Manager -->
        <ImageUploadManager ref="imageUploadManager" :disabled="loading" :max-images="5"
          @images-changed="handleImagesChanged" @error="handleImageError" class="px-2 pt-2" />
        <v-row class="pa-2 pt-0 ma-0">
          <v-col class="pa-0 ma-0">
            <!-- Chat Input -->
            <v-textarea v-model="prompt" label="Ask a question..." rows="1" max-rows="6" auto-grow variant="outlined"
              class="chat-input" hide-details @keydown.enter="handleEnter" @paste="handlePaste" :readonly="loading">
              <template v-slot:prepend>
                <Button color="primary" :click="selectImages" icon="mdi-image-plus" size="large" tooltip="Add images"
                  :disabled="loading" />
              </template>
              <template v-slot:append>
                <div v-if="isStreaming" class="stream-stop-wrapper mr-2">
                  <v-progress-circular color="primary" indeterminate size="32" width="3" />
                  <v-btn class="stream-stop-btn" color="primary" icon size="small" variant="plain"
                    @click="stopStreamingChat">
                    <v-icon size="16">mdi-stop</v-icon>
                  </v-btn>
                </div>
                <v-progress-circular v-else-if="loading" color="primary" indeterminate
                  class="mr-2"></v-progress-circular>
                <Button v-else color="primary" :click="sendMessage" icon="mdi-send" size="x-large" tooltip="Send"
                  :disabled="loading || !prompt.trim()" />
              </template>
              <template v-slot:append-inner>
                <v-progress-circular v-if="voice.state === 'loading-model' || voice.state === 'transcribing'"
                  color="primary" indeterminate size="24" width="2" />
                <Button v-else-if="voice.supported" :color="voice.state === 'recording' ? 'error' : 'primary'"
                  :click="startVoiceInput" :icon="voice.state === 'recording' ? 'mdi-stop' : 'mdi-microphone'"
                  size="large" :tooltip="voiceTooltip" :disabled="loading" />
              </template>
            </v-textarea>
          </v-col>
        </v-row>
      </div>
    </v-card>
    <!-- </v-responsive> -->
  </v-container>
</template>

<script lang="ts">
import { Options, mixins } from 'vue-class-component';
import { nextTick } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import AnalyticsMixin from '@/mixins/AnalyticsMixin';
import {
  cancelChatStream,
  cancelPublicRun,
  type CancelStreamResponse,
  chatWithPublicAgent,
  sendResultsEmail,
  startStreamingChatWithPublicAgent,
  startStreamingPublicAgentSpeak,
  subscribePublicRun,
  type StreamingRequestHandle
} from '@/utils/api';
import { isApiError, SendResultsEmailRequest } from '@/types/api';
import FormattingCard from '@/components/cards/FormattingCard.vue';
import type { MessageRetryFailureKind, MessageRetryState } from '@/types/msq';
import { GenerateResultProviderState, isRefusalFinishReason, ModelMessage, TokenUsage, ToolEvent } from '@/types/msq';
import { UploadedImage, createMessageContent } from '@/utils/imageHandler'
import ImageUploadManager from '@/components/sub/ImageUploadManager.vue'
import Button from '@/components/sub/Button.vue'
import { PublicAgentDetails, PublicChatSession } from '@/types/public';
import { connectSSE, disconnectSSE, addEventListener, removeEventListener, generateSessionId, updateSessionId } from '@/utils/sse';
import ToolEventsPanel from '@/components/sub/ToolEventsPanel.vue'
import { VCardText } from 'vuetify/components';
import { createHash } from '@/utils/general'
import CandlestickChart from '@/components/charts/CandlestickChart.vue'
import AgentShareDialog from '@/components/AgentShareDialog.vue'
import StreamingAudioPlayer from '@/components/sub/StreamingAudioPlayer.vue'
import type { StatusEvent } from '@/types/speak'
import { getMessageText, isChatEmpty } from '@/utils/messages'
import { stripMarkdown } from '@/utils/text'
import { getToolName as getToolNameUtil } from '@/utils/toolEvents'
import { getRefreshTargetsForToolName } from '@/utils/configRefresh'
import { useVoiceInput } from '@/composables/useVoiceInput'
import { extractFinancialData, isFinancialToolEvent, getSymbolFromMetaData } from '@/utils/chart'
import type { FinancialDataResponse } from '@/types/chart'
import { decodeSharedTitleAgent } from '@/utils/summaryAgent'
import { debugLog } from '@/utils/debug'
import { useStreamBuffer } from '@/composables/useStreamBuffer'
import { PtcTranscriptFilter } from '@/utils/ptcSuppression'

type TurnMode = 'text' | 'voice'

interface FailedTurnReplay {
  baselineMessages: ModelMessage[]
  agentUsername: string
  agentSlug: string
  mode: TurnMode
  assistantSourceIndex: number | null
  providerState?: GenerateResultProviderState
  autoRetryAttempted: boolean
}

const KNOWN_ANALYSIS_RETRY_FALLBACK =
  'I retrieved the requested data but encountered an issue completing the analysis. Please try your request again.' as const

@Options({
  props: {
    agent: {
      type: Object as () => PublicAgentDetails,
      required: true,
    },
    session: {
      type: Object as () => PublicChatSession,
      required: true,
    },
    scrollDebug: {
      type: Boolean,
      default: false
    },
    autoRetryFailedResponses: {
      type: Boolean,
      default: true
    },
    // Optional: a message to auto-send once, when this chat mounts into an empty session.
    // Used to kick off a seeded conversation (e.g. starting an automation). Defaults to none.
    initialMessage: {
      type: String,
      default: ''
    }
  },
  components: {
    FormattingCard,
    ToolEventsPanel,
    ImageUploadManager,
    Button,
    CandlestickChart,
    AgentShareDialog,
    StreamingAudioPlayer
  },
  computed: {
    ...mapGetters(['username', 'titleAgent', 'authenticated']),
  },
  methods: {
    ...mapActions(['savePublicChat', 'updatePublicChatTitle']),
  },
  emits: ['initial-message-consumed'],
  watch: {
    // The seed arrives as a prop and is normally read on mount. Also react when it changes on an
    // already-mounted instance — e.g. the dashboard reused the same empty chat without a remount — so
    // the opener is still delivered instead of silently lingering for a later, unrelated chat.
    initialMessage() {
      ;(this as PublicAgentChat).consumeInitialMessage()
    }
  }
})
export default class PublicAgentChat extends mixins(AnalyticsMixin) {
  authenticated!: boolean
  agent!: PublicAgentDetails;
  session!: PublicChatSession;
  scrollDebug!: boolean
  autoRetryFailedResponses!: boolean
  initialMessage!: string
  savePublicChat!: (session: PublicChatSession) => void;
  updatePublicChatTitle!: (session: PublicChatSession) => Promise<boolean>;
  username!: string;
  titleAgent!: string | null;

  localMessages: ModelMessage[] = [];
  prompt = '';
  loading = false;
  voice = useVoiceInput();
  imageUploadManager: InstanceType<typeof ImageUploadManager> | null = null
  // Per-message StreamingAudioPlayer refs (aligned with localMessages indices)
  streamingPlayers: Array<InstanceType<typeof StreamingAudioPlayer> | null> = []
  messageElements: any[] = []
  // Audio streaming state guards to prevent late-appends after end
  audioStreaming: boolean = false
  audioEnded: boolean = false
  // Debug counters
  audioChunkCount: number = 0
  // Fallback watchdog for audio stage (generate and play if no chunks arrive)
  audioStartWatchTimer: number | null = null
  // Guard after first audio chunk to ensure playback actually starts; else force fallback
  audioFirstChunkGuard: number | null = null
  // Global gesture primer (unlocks autoplay policies via WebAudio resume)
  private audioCtxPrimer: (AudioContext | null) = null




  // Streaming and SSE state
  isStreaming = false;
  streamingMessageIndex: number | null = null;
  activeStreamHandle: StreamingRequestHandle | null = null
  streamCancelledByUser = false
  eventListener: ((event: any) => void) | null = null;
  currentSessionId = '';
  agentHash: string = ''
  resizeObserver: ResizeObserver | null = null

  // Buffered streaming state — uses composable for timer management
  private streamBufferUtil: ReturnType<typeof useStreamBuffer> | null = null
  private ptcFilter = new PtcTranscriptFilter()
  // Named timing guards for audio pipeline
  private readonly audioStartWatchMs: number = 600
  private readonly audioFirstChunkGuardMs: number = 1000
  scrollTimer: number | null = null
  private readonly scrollIntervalMs: number = 120
  private scrollDebugEnabled = false
  private scrollDebugLastLogAt = 0
  private readonly scrollDebugThrottleMs = 120
  private readonly scrollDebugMinBottomInsetPx = 8
  private readonly scrollDebugStorageKey = 'msq:scroll-debug'
  private onScrollDebugResize: ((event: UIEvent) => void) | null = null
  private onScrollDebugChatAreaScroll: ((event: Event) => void) | null = null
  private onViewportLayoutChange: ((event: Event) => void) | null = null
  private viewportLayoutRafId: number | null = null
  private autoScrollPaused = false
  private onChatAreaWheel: ((event: Event) => void) | null = null
  private onChatAreaTouchMove: ((event: Event) => void) | null = null
  private toolActivityVisible = false
  private toolActivityMessageIndex: number | null = null
  private activeToolCallCount = 0
  private activeToolEventMessageIndex: number | null = null

  // Resumable run tracking
  currentRunId: string | null = null
  runStreamUnsubscribe: (() => void) | null = null
  private sessionProviderState: GenerateResultProviderState | undefined
  private pendingContextMessages: ModelMessage[] | null = null
  private pendingGeneratedTitle: string | null = null
  // When a chat is seeded with an opener (e.g. from the Automations tab), the first user message is
  // boilerplate. Title from the user's *second* message — their real description of the work — instead.
  private titleFromSecondUserMessage = false
  // Guards consumeInitialMessage so a seeded opener is claimed at most once per chat instance.
  private initialMessageConsumed = false
  private persistingGeneratedTitle = false
  private currentTurnReplay: FailedTurnReplay | null = null
  private currentTurnHadVisibleAssistantContent = false
  private currentTurnHadToolEvents = false
  private currentTurnMessageStopSeen = false
  // Delayed reconciliation timeout
  reconciliationTimeout: number | null = null
  private readonly reconciliationDelayMs: number = 2000

  private filterAssistantTranscriptChunk(chunk: string): string {
    return this.ptcFilter.filterChunk(chunk)
  }

  private sanitizeAssistantTranscriptSnapshot(content: string): { content: string; suppressing: boolean } {
    return this.ptcFilter.sanitizeSnapshot(content)
  }

  private sanitizeAssistantMessages(messages: ModelMessage[]): ModelMessage[] {
    return this.ptcFilter.sanitizeMessages(messages)
  }

  private sanitizeSessionProviderState(
    providerState: GenerateResultProviderState | undefined
  ): GenerateResultProviderState | undefined {
    if (!providerState?.anthropic) {
      return providerState
    }

    const anthropicState: NonNullable<GenerateResultProviderState['anthropic']> = {}
    if (Array.isArray(providerState.anthropic.rawContentBlocks)) {
      anthropicState.rawContentBlocks = providerState.anthropic.rawContentBlocks
    }
    if (providerState.anthropic.assistantTurnBoundary === true) {
      anthropicState.assistantTurnBoundary = true
    }

    return Object.keys(anthropicState).length > 0 ? { anthropic: anthropicState } : undefined
  }

  private hasReplayableAnthropicAssistantState(message: Pick<ModelMessage, 'providerState' | 'rawContentBlocks'>): boolean {
    if (message.providerState?.anthropic?.assistantTurnBoundary === true) {
      return true
    }

    const providerStateRawBlocks = message.providerState?.anthropic?.rawContentBlocks
    if (Array.isArray(providerStateRawBlocks) && providerStateRawBlocks.length > 0) {
      return true
    }

    return Array.isArray(message.rawContentBlocks) && message.rawContentBlocks.length > 0
  }

  private hasDisplayableMessageContent(message: Pick<ModelMessage, 'content'>): boolean {
    if (typeof message.content === 'string') {
      return message.content.trim().length > 0
    }

    return Array.isArray(message.content) && message.content.length > 0
  }

  private shouldKeepConversationMessage(message: ModelMessage): boolean {
    if (message.role === 'tool') {
      return true
    }

    if (message.role === 'assistant') {
      if (Array.isArray(message.tool_calls) && message.tool_calls.length > 0) {
        return true
      }
      if (this.hasReplayableAnthropicAssistantState(message)) {
        return true
      }
      if (Array.isArray(message.toolEvents) && message.toolEvents.length > 0) {
        return true
      }
    }

    return this.hasDisplayableMessageContent(message)
  }

  private stripRetryMetadata(message: ModelMessage): ModelMessage {
    const { retryState, ...rest } = message
    return { ...rest }
  }

  private shouldRenderMessage(message: ModelMessage, sourceIndex: number): boolean {
    if (message.role === 'tool') return false
    if (message.role === 'assistant' && Array.isArray(message.tool_calls) && message.tool_calls.length > 0) return false
    if (
      message.role === 'assistant' &&
      sourceIndex !== this.streamingMessageIndex &&
      !this.hasDisplayableMessageContent(message) &&
      (!Array.isArray(message.toolEvents) || message.toolEvents.length === 0) &&
      // A refused turn can legitimately carry no content; it still has to render so its notice shows.
      !isRefusalFinishReason(message.finishReason)
    ) {
      return false
    }
    return true
  }

  /**
   * True when a completed assistant turn ended in a model refusal, so the transcript should show the
   * refusal notice for it. A normal turn ('stop'/'tool_calls'/'length'/no reason) is never affected.
   */
  private isRefusedMessage(message: ModelMessage): boolean {
    return message.role === 'assistant' && isRefusalFinishReason(message.finishReason)
  }

  /**
   * Records a refusal on the completed assistant message so the transcript can render an
   * in-conversation notice for it. Deliberately stores nothing for normal finish reasons: the field
   * stays absent unless the turn was refused, keeping every other message byte-identical to before.
   */
  private applyRefusalFinishReason(sourceIndex: number | null, finishReason?: string | null): void {
    if (sourceIndex === null) return
    if (!isRefusalFinishReason(finishReason)) return
    const current = this.localMessages[sourceIndex]
    if (!current || current.role !== 'assistant') return
    this.localMessages.splice(sourceIndex, 1, { ...current, finishReason })
  }

  private shouldShowRetryButton(message: ModelMessage, sourceIndex: number): boolean {
    if (message.role !== 'assistant') return false
    if (!message.retryState?.manualRetryAvailable) return false
    if (this.loading) return false
    return sourceIndex === this.localMessages.length - 1
  }

  private cloneMessages(messages: ModelMessage[]): ModelMessage[] {
    return structuredClone(messages)
  }

  private clearRetryStateFromMessages(messages: ModelMessage[]): ModelMessage[] {
    return messages.map((message) => this.stripRetryMetadata(message))
  }

  private sanitizeLoadedMessages(messages: ModelMessage[]): ModelMessage[] {
    return this
      .sanitizeAssistantMessages(this.clearRetryStateFromMessages(this.cloneMessages(messages)))
      .filter((message) => this.shouldKeepConversationMessage(message))
  }

  private resetCurrentTurnProgress(): void {
    this.currentTurnHadVisibleAssistantContent = false
    this.currentTurnHadToolEvents = false
    this.currentTurnMessageStopSeen = false
  }

  private markCurrentTurnProgress(params: { visibleAssistantDelta?: boolean; toolEvent?: boolean; messageStop?: boolean }): void {
    if (params.visibleAssistantDelta) {
      this.currentTurnHadVisibleAssistantContent = true
    }
    if (params.toolEvent) {
      this.currentTurnHadToolEvents = true
    }
    if (params.messageStop) {
      this.currentTurnMessageStopSeen = true
    }
  }

  private clearCurrentTurnReplay(): void {
    this.currentTurnReplay = null
    this.resetCurrentTurnProgress()
  }

  private clearManualRetryAvailability(): void {
    this.localMessages = this.localMessages.map((message) => {
      if (!message.retryState) return message
      return {
        ...message,
        retryState: {
          ...message.retryState,
          manualRetryAvailable: false,
          autoRetryPending: false
        }
      }
    })
  }

  private classifyTurnFailure(errorMessage: string, mode: TurnMode): MessageRetryFailureKind {
    const normalized = errorMessage.toLowerCase()
    if (mode === 'voice' && normalized.includes('audio')) {
      return 'audio'
    }
    if (normalized.includes('timed out') || normalized.includes('timeout')) {
      return 'timeout'
    }
    if (normalized.includes('provider') || normalized.includes('api error') || normalized.includes('[anthropic]')) {
      return 'provider'
    }
    if (normalized.includes('stream')) {
      return 'stream'
    }
    return 'unknown'
  }

  private isSafeAutoRetryCandidate(mode: TurnMode): boolean {
    if (this.streamCancelledByUser) return false
    if (!this.autoRetryFailedResponses) return false
    if (!this.currentTurnReplay) return false
    if (this.currentTurnReplay.autoRetryAttempted) return false
    if (this.currentTurnHadToolEvents) return false
    if (this.currentTurnHadVisibleAssistantContent) return false
    if (this.currentTurnMessageStopSeen) return false
    if (mode === 'voice') return false
    return true
  }

  private applyFailedAssistantState(sourceIndex: number, retryState: MessageRetryState): void {
    const current = this.localMessages[sourceIndex]
    if (!current) return
    this.localMessages.splice(sourceIndex, 1, {
      ...current,
      retryState
    })
  }

  private isKnownRetryableCompletionFallback(content: string): boolean {
    return content.includes(KNOWN_ANALYSIS_RETRY_FALLBACK)
  }

  private markKnownRetryableCompletion(sourceIndex: number, mode: TurnMode): boolean {
    const current = this.localMessages[sourceIndex]
    if (!current || current.role !== 'assistant' || typeof current.content !== 'string') {
      return false
    }

    if (!this.isKnownRetryableCompletionFallback(current.content)) {
      return false
    }

    this.applyFailedAssistantState(sourceIndex, {
      failed: true,
      retryable: false,
      manualRetryAvailable: true,
      autoRetryAttempted: this.currentTurnReplay?.autoRetryAttempted === true,
      autoRetryPending: false,
      failureKind: this.classifyTurnFailure(current.content, mode),
      errorMessage: KNOWN_ANALYSIS_RETRY_FALLBACK
    })
    return true
  }

  private resetPtcTranscriptSuppression(): void {
    this.ptcFilter.reset()
  }

  private showToolActivity(messageIndex: number): void {
    if (this.toolActivityMessageIndex !== messageIndex) {
      this.activeToolCallCount = 0
    }
    this.toolActivityMessageIndex = messageIndex
    this.toolActivityVisible = true
  }

  private hideToolActivity(): void {
    this.toolActivityVisible = false
    this.toolActivityMessageIndex = null
    this.activeToolCallCount = 0
  }

  private handleToolActivityEvent(eventType: string): void {
    if (this.activeToolEventMessageIndex === null) return

    if (eventType === 'tool_start') {
      this.activeToolCallCount += 1
      this.showToolActivity(this.activeToolEventMessageIndex)
      return
    }

    if (eventType === 'tool_end' || eventType === 'tool_error') {
      this.activeToolCallCount = Math.max(0, this.activeToolCallCount - 1)
    }
  }

  private handleResponseStreamingStarted(): void {
    if (!this.toolActivityVisible) return
    this.hideToolActivity()
  }

  // Throttle scroll to bottom to avoid layout thrash.
  // MUST be a real method, not a class-property arrow: vue-class-component harvests property
  // arrows off a bare `new Ctor(props, ctx)` instance whose `this` only ever gets
  // $props/$attrs/$slots/$emit — never $refs or $el — so an arrow here silently loses the chat
  // area (resolveChatAreaElement() returns null) and auto-scroll no-ops on every call. Methods
  // are collected into options.methods and bound to the component proxy, where $refs resolves.
  scheduleScroll(): void {
    if (this.scrollTimer != null) return
    this.logScrollSnapshot('scheduleScroll.queued', { delayMs: this.scrollIntervalMs })
    this.scrollTimer = window.setTimeout(() => {
      this.logScrollSnapshot('scheduleScroll.execute')
      this.scrollToBottom('scheduleScroll')
      this.scrollTimer = null
    }, this.scrollIntervalMs)
  }

  get hasAgentSelector(): boolean {
    return !!this.$slots['agent-selector']
  }

  get showScrollToBottomBtn(): boolean {
    return this.autoScrollPaused && this.isStreaming
  }

  get displayMessages(): Array<{ message: ModelMessage; sourceIndex: number }> {
    return this.localMessages
      .map((message, sourceIndex) => ({ message, sourceIndex }))
      .filter(({ message, sourceIndex }) => this.shouldRenderMessage(message, sourceIndex))
  }

  private applyPendingContextMessagesToStreamingMessage(): number | null {
    if (this.streamingMessageIndex === null || !this.pendingContextMessages || this.pendingContextMessages.length === 0) {
      return this.streamingMessageIndex
    }

    const streamingMessageIndex = this.streamingMessageIndex
    const currentStreamingMessage = this.localMessages[streamingMessageIndex]
    if (!currentStreamingMessage) {
      this.pendingContextMessages = null
      return null
    }

    const normalizedContextMessages: ModelMessage[] = this.pendingContextMessages
      .map((message) => ({
        ...message,
        content: message.content ?? ''
      }))
      .filter((message) => this.shouldKeepConversationMessage(message))

    const authoritativeFinalAssistant = normalizedContextMessages[normalizedContextMessages.length - 1]
    const priorContextMessages = normalizedContextMessages.slice(0, -1)

    const mergedFinalAssistant: ModelMessage =
      authoritativeFinalAssistant && authoritativeFinalAssistant.role === 'assistant'
        ? {
            ...currentStreamingMessage,
            ...authoritativeFinalAssistant,
            content: authoritativeFinalAssistant.content ?? currentStreamingMessage.content ?? '',
            toolEvents: currentStreamingMessage.toolEvents ?? authoritativeFinalAssistant.toolEvents,
            tokenUsage: currentStreamingMessage.tokenUsage ?? authoritativeFinalAssistant.tokenUsage
          }
        : currentStreamingMessage

    this.localMessages.splice(streamingMessageIndex, 1, mergedFinalAssistant)

    if (priorContextMessages.length > 0) {
      this.localMessages.splice(streamingMessageIndex, 0, ...priorContextMessages)
    }

    this.pendingContextMessages = null
    return streamingMessageIndex + priorContextMessages.length
  }

  private beginToolEventRouting(messageIndex: number): void {
    this.activeToolEventMessageIndex = messageIndex
  }

  private clearToolEventRouting(): void {
    this.activeToolEventMessageIndex = null
  }

  // Sharing UI state
  showShare = false
  ownerUsername = ''
  agentSlug = ''

  async created() {
    this.localMessages = this.sanitizeLoadedMessages(this.session.messages)
    this.sessionProviderState = this.sanitizeSessionProviderState(this.session.providerState)
    this.streamBufferUtil = useStreamBuffer((bufferedContent: string) => {
      if (this.streamingMessageIndex === null) return
      const idx = this.streamingMessageIndex
      const updatedMessage = { ...this.localMessages[idx] }
      updatedMessage.content += bufferedContent
      this.localMessages.splice(idx, 1, updatedMessage)
      this.scheduleScroll()
    })
  }

  // Deliver a one-time seeded opener (e.g. starting an automation from the Automations tab). It's
  // claimed exactly once per chat instance: we emit `initial-message-consumed` as soon as we observe a
  // seed — whether we send it or not — so the parent clears it and it can never be re-applied to a
  // different chat the user opens later. It's only actually sent into a brand-new (empty) conversation.
  private consumeInitialMessage(): void {
    if (this.initialMessageConsumed) return
    const seed = this.initialMessage
    if (!seed || !seed.trim()) return
    this.initialMessageConsumed = true
    this.$emit('initial-message-consumed')
    if (this.localMessages.length !== 0) return
    // The seeded opener is generic boilerplate; title this chat from the user's next (real) message.
    this.titleFromSecondUserMessage = true
    nextTick(() => {
      this.prompt = seed
      void this.sendMessage()
    })
  }

  async mounted(): Promise<void> {
    this.agentHash = await createHash(this.agent)
    this.trackComponentView('public_agent_chat', { agent_id: this.agentHash, agent_name: this.agent.name });
    this.setupSSE();
    
    // Attempt to fetch server-side chat history (feature-flagged in store)
    try {
      const pathParts = this.$route?.path.split('/').filter((p: string) => p) || []
      const username = pathParts[1]
      const agentslug = pathParts[2]
      this.ownerUsername = username || ''
      this.agentSlug = agentslug || ''
      if (username && agentslug) {
        await this.$store.dispatch('fetchPublicChatHistoryForAgent', { agentUsername: username, agentSlug: agentslug })
      }
      // After owner + slug resolution, attempt to auto-resume any pending run
      this.tryAutoResumeRun()
    } catch (e) {
      // no-op
    }

    // Deliver any seeded opener for this fresh chat. Extracted so the prop watcher can also trigger it
    // when the dashboard reused the same chat without a remount (see consumeInitialMessage).
    this.consumeInitialMessage()

    // Get reference to image upload manager
    nextTick(() => {
      this.imageUploadManager = this.$refs.imageUploadManager as InstanceType<typeof ImageUploadManager>
      this.scrollDebugEnabled = this.resolveScrollDebugEnabled()
      debugLog('[CHAT_SCROLL_DEBUG] mode', {
        enabled: this.scrollDebugEnabled,
        viaProp: this.scrollDebug === true,
        storageKey: this.scrollDebugStorageKey
      })
      
      // Setup resize observer for responsive charts
      const chatArea = this.resolveChatAreaElement()
      if (chatArea && chatArea instanceof Element) {
        this.resizeObserver = new ResizeObserver(() => {
          // Force update to recalculate chart dimensions
          this.$forceUpdate()
          this.logScrollSnapshot('resizeObserver.chatArea')
        })
        this.resizeObserver.observe(chatArea)

        if (this.scrollDebugEnabled) {
          this.onScrollDebugChatAreaScroll = () => {
            this.logScrollSnapshot('chatArea.scroll')
          }
          chatArea.addEventListener('scroll', this.onScrollDebugChatAreaScroll, { passive: true })
        }
      }

      if (this.scrollDebugEnabled) {
        this.onScrollDebugResize = () => {
          this.logScrollSnapshot('window.resize')
        }
        window.addEventListener('resize', this.onScrollDebugResize, { passive: true })
        this.logScrollSnapshot('mounted.initial', {
          activation: {
            queryFlag: new URLSearchParams(window.location.search).get('scrollDebug'),
            storageKey: this.scrollDebugStorageKey
          }
        }, true)
      }

      this.scheduleViewportHeightSync()

      // User scroll detection: pause content-driven auto-scroll during streaming.
      if (chatArea) {
        const pauseAutoScroll = () => {
          if (!this.isStreaming || this.autoScrollPaused) return
          this.autoScrollPaused = true
          this.stopStreamAutoFollow()
        }
        this.onChatAreaWheel = pauseAutoScroll
        this.onChatAreaTouchMove = pauseAutoScroll
        chatArea.addEventListener('wheel', this.onChatAreaWheel, { passive: true })
        chatArea.addEventListener('touchmove', this.onChatAreaTouchMove, { passive: true })
      }
    })

    this.onViewportLayoutChange = () => {
      this.scheduleViewportHeightSync()
    }
    window.addEventListener('resize', this.onViewportLayoutChange, { passive: true })
    window.addEventListener('scroll', this.onViewportLayoutChange, { passive: true })
  }

  beforeUpdate() {
    this.messageElements = []
  }

  updated(): void {
    this.scheduleViewportHeightSync()
  }

  beforeUnmount(): void {
    // Release the mic if the user navigates away mid-recording (discards the take).
    void this.voice.cancel()
    if (this.activeStreamHandle) {
      this.activeStreamHandle.controller.abort()
      this.activeStreamHandle = null
    }
    this.cleanupSSE();
    // Ensure run subscription is cleaned up
    this.clearCurrentRunId()
    
    // Clean up resize observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    const chatArea = this.resolveChatAreaElement()
    if (chatArea) {
      if (this.onScrollDebugChatAreaScroll) {
        chatArea.removeEventListener('scroll', this.onScrollDebugChatAreaScroll)
        this.onScrollDebugChatAreaScroll = null
      }
      if (this.onChatAreaWheel) {
        chatArea.removeEventListener('wheel', this.onChatAreaWheel)
        this.onChatAreaWheel = null
      }
      if (this.onChatAreaTouchMove) {
        chatArea.removeEventListener('touchmove', this.onChatAreaTouchMove)
        this.onChatAreaTouchMove = null
      }
    }
    if (this.onScrollDebugResize) {
      window.removeEventListener('resize', this.onScrollDebugResize)
      this.onScrollDebugResize = null
    }
    if (this.onViewportLayoutChange) {
      window.removeEventListener('resize', this.onViewportLayoutChange)
      window.removeEventListener('scroll', this.onViewportLayoutChange)
      this.onViewportLayoutChange = null
    }
    this.stopStreamAutoFollow()
    if (this.viewportLayoutRafId != null) {
      window.cancelAnimationFrame(this.viewportLayoutRafId)
      this.viewportLayoutRafId = null
    }
  }

  // Image handling methods
  selectImages(): void {
    if (this.imageUploadManager) {
      this.imageUploadManager.selectImages()
    }
  }

  handleImagesChanged(images: UploadedImage[]): void {
    // Images are managed by the ImageUploadManager component
    // This is just for any additional logic if needed
  }

  handleImageError(error: string): void {
    this.trackEvent('public_chat_image_error', { agent_id: this.agentHash, error_message: error });
    this.$store.dispatch('updateSnackbar', {
      text: error,
      color: 'error',
      timeout: 3000
    })
  }

  // Route image data from a clipboard paste through the same pipeline as the
  // file picker so pasted screenshots show up as thumbnails and are sent
  // identically to picker-selected images.
  handlePaste(event: ClipboardEvent): void {
    if (this.loading) return
    const items = event.clipboardData?.items
    if (!items || items.length === 0) return

    const imageFiles: File[] = []
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) imageFiles.push(file)
      }
    }

    if (imageFiles.length === 0) return

    // Only suppress the default paste when we actually consumed image data,
    // so mixed text+image (or text-only) pastes still insert their text.
    event.preventDefault()
    this.trackEvent('public_chat_image_pasted', {
      agent_id: this.agentHash,
      image_count: imageFiles.length
    })
    // Surface any unexpected rejection from addFiles so the user gets a
    // snackbar instead of a silently swallowed promise.
    this.imageUploadManager?.addFiles(imageFiles).catch((err: unknown) => {
      const message = err instanceof Error ? err.message : String(err)
      this.handleImageError(message)
    })
  }

  // Capture StreamingAudioPlayer instance per message index
  setStreamingPlayerRef(index: number, el: any): void {
    const ok = el && typeof el.prime === 'function' && typeof el.loadBytes === 'function'
    this.streamingPlayers[index] = ok ? (el as any) : null
  }

  private getCurrentStreamingPlayer(): InstanceType<typeof StreamingAudioPlayer> | null {
    if (this.streamingMessageIndex === null) return null
    return this.streamingPlayers[this.streamingMessageIndex] || null
  }

  // Prime autoplay under the exact user gesture (send button). This unlocks media playback policies.
  private primeAutoplayGesture(): void {
    try {
      // Prime any existing per-message players synchronously
      for (const p of this.streamingPlayers) {
        try { p?.prime() } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    try {
      // Also resume a WebAudio context to satisfy certain browser policies
      const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
        || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (Ctx) {
        if (!this.audioCtxPrimer) this.audioCtxPrimer = new Ctx()
        // Resume if suspended
        this.audioCtxPrimer?.resume()?.catch(() => { /* ignore */ })
      }
    } catch { /* ignore */ }
  }

  // SSE/UI event frame guard
  private isUiToolEventFrame(v: unknown): v is { type: string; message?: string; data?: unknown; sessionId?: string; clientId?: string } {
    return typeof v === 'object' && v != null && 'type' in (v as any)
  }

  setupSSE(): void {
    // Connect on mount, but session ID will be updated per message
    connectSSE(generateSessionId('public-user')); 

    this.eventListener = (event: any) => {
      // The session ID for the *current* chat interaction is stored in this.currentSessionId
      if (this.isUiToolEventFrame(event) && event.sessionId === this.currentSessionId && this.activeToolEventMessageIndex !== null) {
        const toolEvent: ToolEvent = {
          type: event.type,
          message: event.message,
          data: event.data,
          sessionId: event.sessionId,
          clientId: event.clientId,
          timestamp: Date.now()
        }
        
        // Add to the current streaming message's toolEvents
        const currentMessage = this.localMessages[this.activeToolEventMessageIndex]
        if (!currentMessage) {
          return
        }
        this.markCurrentTurnProgress({ toolEvent: true })
        if (!currentMessage.toolEvents) {
          currentMessage.toolEvents = []
        }
        currentMessage.toolEvents.push(toolEvent)
        this.handleToolActivityEvent(event.type)
        
        this.trackEvent('public_chat_tool_event', {
            agent_id: this.agentHash,
            tool_event_type: event.type,
            tool_name: getToolNameUtil(event),
            is_error: event.type === 'tool_error'
        });
        if (event.type === 'tool_end') {
          const dirtyTargets = getRefreshTargetsForToolName(getToolNameUtil(event))
          if (dirtyTargets.length > 0) {
            this.$store.dispatch('markConfigResourcesDirty', dirtyTargets)
          }
        }
        this.scrollToBottom();
      }
    };
    addEventListener(this.eventListener);
  }

  cleanupSSE(): void {
    if (this.eventListener) {
      removeEventListener(this.eventListener);
      this.eventListener = null;
    }
    disconnectSSE();
    this.clearToolEventRouting()
  }

  handleEnter(event: KeyboardEvent): void {
    if (!event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  get activeRunId(): string | null {
    return this.currentRunId ?? this.activeStreamHandle?.runId ?? null
  }

  private clearStreamingTimers(): void {
    this.streamBufferUtil?.reset()
    if (this.audioStartWatchTimer != null) {
      window.clearTimeout(this.audioStartWatchTimer)
      this.audioStartWatchTimer = null
    }
    if (this.audioFirstChunkGuard != null) {
      window.clearTimeout(this.audioFirstChunkGuard)
      this.audioFirstChunkGuard = null
    }
    if (this.reconciliationTimeout != null) {
      window.clearTimeout(this.reconciliationTimeout)
      this.reconciliationTimeout = null
    }
  }
  get voiceTooltip(): string {
    return this.voice.state === 'recording' ? 'Stop and transcribe' : 'Voice input'
  }

  async startVoiceInput(): Promise<void> {
    try {
      const text = await this.voice.toggle()
      if (this.voice.engine === 'server') {
        // The take was transcribed by the server-side relay instead of the browser-local model.
        // engine resets at the next record start, so this fires once per fallback take.
        this.trackEvent('public_chat_voice_fallback', { agent_id: this.agentHash })
      }
      if (text) {
        this.prompt = this.prompt.trim() ? `${this.prompt.trimEnd()} ${text}` : text
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      this.trackEvent('public_chat_voice_input_error', { agent_id: this.agentHash, error_message: message })
      this.$store.dispatch('updateSnackbar', {
        text: message,
        color: 'error',
        timeout: 5000
      })
    }
  }

  private finalizeLocalCancellation(): void {
    this.logScrollSnapshot('stream.cancel.finalize.start', {}, true)
    this.clearStreamingTimers()
    this.stopStreamAutoFollow()

    this.streamBufferUtil?.flush()

    const player = this.getCurrentStreamingPlayer()
    try { player?.endStream() } catch { /* ignore */ }

    this.audioStreaming = false
    this.audioEnded = true
    this.isStreaming = false
    this.loading = false
    this.streamingMessageIndex = null
    this.activeStreamHandle = null
    this.updateSession()
    this.scrollToBottom('stream.cancel.finalize')
    this.clearCurrentRunId()
    this.clearCurrentTurnReplay()
    this.resetPtcTranscriptSuppression()
    this.hideToolActivity()
    this.logScrollSnapshot('stream.cancel.finalize.done', {}, true)
  }

  private wasServerCancellationConfirmed(response: CancelStreamResponse | unknown): boolean {
    if (isApiError(response)) return false
    if (!response || typeof response !== 'object') return false

    const payload = response as {
      success?: unknown
      found?: unknown
      cancelled?: unknown
      alreadyCancelled?: unknown
    }

    if (payload.success !== true) return false

    const hasResultFlags =
      typeof payload.cancelled === 'boolean'
      || typeof payload.alreadyCancelled === 'boolean'
      || typeof payload.found === 'boolean'

    if (!hasResultFlags) return true
    return payload.cancelled === true || payload.alreadyCancelled === true
  }

  private buildReplayBaselineAfterUserAppend(): ModelMessage[] {
    return this.cloneMessages(this.localMessages)
  }

  private async startTurnWithPreparedConversation(params: {
    baselineMessages: ModelMessage[]
    agentUsername: string
    agentSlug: string
    mode: TurnMode
    autoRetryAttempted: boolean
  }): Promise<void> {
    const baselineMessages = this.clearRetryStateFromMessages(this.cloneMessages(params.baselineMessages))
    this.localMessages = baselineMessages

    const assistantMessageIndex = this.localMessages.length
    this.localMessages.push({ role: 'assistant', content: '' })
    this.streamingMessageIndex = assistantMessageIndex
    this.beginToolEventRouting(assistantMessageIndex)
    this.resetPtcTranscriptSuppression()
    this.hideToolActivity()
    this.resetCurrentTurnProgress()

    this.loading = true
    this.isStreaming = false
    this.streamCancelledByUser = false
    this.activeStreamHandle = null
    this.pendingContextMessages = null

    this.currentTurnReplay = {
      baselineMessages: this.cloneMessages(baselineMessages),
      agentUsername: params.agentUsername,
      agentSlug: params.agentSlug,
      mode: params.mode,
      assistantSourceIndex: assistantMessageIndex,
      providerState: this.sessionProviderState,
      autoRetryAttempted: params.autoRetryAttempted
    }

    nextTick(() => {
      try {
        const player = this.getCurrentStreamingPlayer()
        player?.prime()
      } catch { /* ignore */ }
    })

    this.scrollToBottom()
    this.currentSessionId = generateSessionId('public-user')
    updateSessionId(this.currentSessionId)

    await this.streamChat(params.agentUsername, params.agentSlug)
  }

  private async retryFailedAssistantResponse(sourceIndex: number, mode: 'manual' | 'auto'): Promise<void> {
    if (this.loading) return
    const replay = this.currentTurnReplay
    if (!replay) return
    if (replay.assistantSourceIndex !== sourceIndex) return
    if (sourceIndex !== this.localMessages.length - 1) return

    const current = this.localMessages[sourceIndex]
    if (!current || current.role !== 'assistant') return

    const autoRetryAttempted = replay.autoRetryAttempted || mode === 'auto'
    const baselineMessages = this.cloneMessages(replay.baselineMessages)

    await this.startTurnWithPreparedConversation({
      baselineMessages,
      agentUsername: replay.agentUsername,
      agentSlug: replay.agentSlug,
      mode: replay.mode,
      autoRetryAttempted
    })
  }

  async stopStreamingChat(): Promise<void> {
    if (!this.isStreaming) return

    this.logScrollSnapshot('stream.stop.requested', { activeRunId: this.activeRunId }, true)
    this.streamCancelledByUser = true
    const runId = this.activeRunId
    const sessionId = this.currentSessionId

    if (this.activeStreamHandle) {
      this.activeStreamHandle.controller.abort()
    }
    this.finalizeLocalCancellation()

    let backendCancellationConfirmed = false

    if (runId) {
      try {
        const response = await cancelPublicRun(runId)
        backendCancellationConfirmed = this.wasServerCancellationConfirmed(response)
        if (!backendCancellationConfirmed) {
          console.warn('[PublicAgentChat] cancelPublicRun did not confirm cancellation', { runId, response })
        }
      } catch {
        console.warn('[PublicAgentChat] cancelPublicRun threw unexpectedly', { runId })
      }
    }

    if (!backendCancellationConfirmed && sessionId) {
      try {
        const response = await cancelChatStream(sessionId, runId ?? undefined)
        backendCancellationConfirmed = this.wasServerCancellationConfirmed(response)
        if (!backendCancellationConfirmed) {
          console.warn('[PublicAgentChat] cancelChatStream did not confirm cancellation', { sessionId, runId, response })
        }
      } catch {
        console.warn('[PublicAgentChat] cancelChatStream threw unexpectedly', { sessionId, runId })
      }
    }

    if (!backendCancellationConfirmed) {
      console.warn('[PublicAgentChat] Backend cancellation could not be confirmed', { sessionId, runId })
    }
  }

  async sendMessage() {
    this.trackButtonClick('public_chat_send_message', 'click', {
        agent_id: this.agentHash,
        message_length: this.prompt.trim().length,
        image_count: this.imageUploadManager?.getImages().length || 0
    });
    if (((!this.prompt.trim()) && !(this.imageUploadManager?.getImages().length)) || this.loading) return;

    this.resetAutoScrollState()
    this.clearManualRetryAvailability()
    this.clearCurrentTurnReplay()

    // IMPORTANT: Prime autoplay synchronously within the user gesture (click) before any awaits.
    this.primeAutoplayGesture()

    // Get current prompt and images
    const currentPrompt = this.prompt;
    const currentImages = this.imageUploadManager?.getImages() || [];
    
    // Clear inputs immediately
    this.prompt = '';
    this.imageUploadManager?.clearImages();
    
    // Create message content
    const messageContent = createMessageContent(currentPrompt, currentImages);
    
    const userMessage: ModelMessage = { 
      role: 'user', 
      content: messageContent 
    };
    
    this.localMessages.push(userMessage);
    this.updateSession();

    // Generate the title once, from the message that actually describes the chat. Normally that's the
    // first user message; for seeded chats (an Automations opener) the first message is boilerplate, so
    // wait for the second user message. generateChatTitle no-ops while the title is still "New Chat",
    // so the skipped seeded message leaves the chat untitled until the real one arrives.
    const userMessageCount = this.localMessages.filter((m) => m.role === 'user').length;
    const titleAtUserMessage = this.titleFromSecondUserMessage ? 2 : 1;
    if (userMessageCount === titleAtUserMessage) {
      this.generateChatTitle(currentPrompt);  // Don't await - let it run async
    }

    // Use session data instead of parsing route (works in both public and dashboard contexts)
    const username = this.session.agentUsername;
    const agentslug = this.session.agentSlug;

    if (!username || !agentslug) {
      console.error('Agent username or slug is not available');
      this.$store.dispatch('updateSnackbar', { text: 'Error: Could not determine agent details.', color: 'error' });
      this.loading = false;
      return;
    }

    const baselineMessages = this.buildReplayBaselineAfterUserAppend()
    const mode: TurnMode = this.agent.hasVoice === true ? 'voice' : 'text'
    await this.startTurnWithPreparedConversation({
      baselineMessages,
      agentUsername: username,
      agentSlug: agentslug,
      mode,
      autoRetryAttempted: false
    });
  }

  async streamChat(username: string, slug: string) {
  debugLog('[PublicAgentChat] streamChat start', { 
    username, 
    slug, 
    messagesCount: this.localMessages.length,
    hasVoice: this.agent.hasVoice
  })
  this.logScrollSnapshot('streamChat.start', {
    username,
    slug,
    hasVoice: this.agent.hasVoice,
    messagesCount: this.localMessages.length
  }, true)

  // Check if agent has voice capability
  const useVoiceEndpoint = this.agent.hasVoice === true

  if (useVoiceEndpoint) {
    // Voice-enabled path (existing implementation)
    debugLog('[PublicAgentChat] Using voice endpoint')
    this.isStreaming = true
    this.startStreamAutoFollow()
    // Declare player in scope accessible to callbacks
    let player: InstanceType<typeof StreamingAudioPlayer> | null = null
    // Ensure the per-message player is mounted, then reset its stream
    try { 
      await nextTick()
      player = this.getCurrentStreamingPlayer()
      debugLog('[PublicAgentChat] player ready', { hasPlayer: !!player })
    } catch (e) { 
      console.error('[PublicAgentChat] resetStream error', e)
    }
    this.audioStreaming = true
    this.audioEnded = false
    this.audioChunkCount = 0

    const callbacks = {
    onTextDelta: (chunk: string) => {
      debugLog('[PublicAgentChat] onTextDelta', { len: chunk.length, streamingIndex: this.streamingMessageIndex })
      this.logScrollSnapshot('stream.voice.onTextDelta', {
        chunkLen: chunk.length,
        streamBufferLen: 0
      })
      const visibleChunk = this.filterAssistantTranscriptChunk(chunk)
      if (visibleChunk.length > 0) {
        this.markCurrentTurnProgress({ visibleAssistantDelta: true })
        this.handleResponseStreamingStarted()
      }
      if (this.streamingMessageIndex !== null) {
        this.streamBufferUtil?.append(visibleChunk)
      }
    },
    onMessageStop: () => {
      debugLog('[PublicAgentChat] onMessageStop')
      this.logScrollSnapshot('stream.voice.onMessageStop', {
        pendingBufferLen: 0,
        audioChunkCount: this.audioChunkCount
      }, true)
      // Text stream is complete. Flush any buffered text immediately and update UI state.
      this.markCurrentTurnProgress({ messageStop: true })
      this.streamBufferUtil?.flush()

      // Schedule delayed reconciliation to allow backend DB flush to complete
      // Clear any existing reconciliation timeout
      if (this.reconciliationTimeout != null) {
        window.clearTimeout(this.reconciliationTimeout)
        this.reconciliationTimeout = null
      }
      // Schedule reconciliation after 2 seconds (allows backend to flush to DB)
      this.reconciliationTimeout = window.setTimeout(() => {
        debugLog('[PublicAgentChat] Delayed reconciliation triggered')
        this.reconcileWithRunSnapshot()
        this.reconciliationTimeout = null
      }, this.reconciliationDelayMs)

      // If no audio chunks have arrived shortly after text completes, synthesize and play fallback
      if (this.audioStartWatchTimer != null) {
        window.clearTimeout(this.audioStartWatchTimer)
        this.audioStartWatchTimer = null
      }
      this.audioStartWatchTimer = window.setTimeout(async () => {
        try {
          if (!this.audioEnded && this.audioChunkCount === 0 && this.streamingMessageIndex !== null) {
            console.warn('[PublicAgentChat] No audio after message_stop, triggering fallback TTS')
            await this.fallbackGenerateAndPlayCurrent(this.streamingMessageIndex)
            // Note: Don't call onAudioStop here - let the fallback audio play naturally
            // The UI state will be cleaned up when audio actually completes or via finally block
          }
        } finally {
          this.audioStartWatchTimer = null
        }
      }, this.audioStartWatchMs)
    },
    onAudioChunk: (b64: string) => {
      if (!this.audioStreaming || this.audioEnded) return
      this.audioChunkCount++
      debugLog('[PublicAgentChat] onAudioChunk', { chunk: this.audioChunkCount, b64Len: b64.length })
      // First real audio arrived; cancel fallback watchdog if present
      if (this.audioStartWatchTimer != null) {
        window.clearTimeout(this.audioStartWatchTimer)
        this.audioStartWatchTimer = null
      }
      // Initialize the player stream on first chunk (defer MSE/Blob setup until bytes exist)
      if (this.audioChunkCount === 1) {
        try { player?.resetStream() } catch { /* ignore */ }
      }
      player?.appendChunk(b64)
      // Ensure playback is kicked off when first chunks arrive (in case autoplay was blocked)
      try {
        if (player && !player.isPlaying) {
          player.playAudio()
        }
      } catch { /* ignore */ }

      // If this is the first chunk, schedule a guard to verify playback actually starts.
      if (this.audioChunkCount === 1) {
        if (this.audioFirstChunkGuard != null) {
          window.clearTimeout(this.audioFirstChunkGuard)
          this.audioFirstChunkGuard = null
        }
        this.audioFirstChunkGuard = window.setTimeout(async () => {
          try {
            if (!this.audioEnded && player && !player.isPlaying && this.streamingMessageIndex !== null) {
              console.warn('[PublicAgentChat] First chunk arrived but playback did not start; forcing fallback TTS')
              // Stop current streaming pipeline
              this.audioStreaming = false
              this.audioEnded = true
              try { player.endStream() } catch (_e) { /* ignore */ }
              await this.fallbackGenerateAndPlayCurrent(this.streamingMessageIndex)
            }
          } finally {
            this.audioFirstChunkGuard = null
          }
        }, this.audioFirstChunkGuardMs)
      }

    },
    onAudioStop: () => {
      if (this.audioEnded) return
      this.audioEnded = true
      // Cancel any pending fallbacks and reconciliation
      if (this.audioStartWatchTimer != null) {
        window.clearTimeout(this.audioStartWatchTimer)
        this.audioStartWatchTimer = null
      }
      if (this.audioFirstChunkGuard != null) {
        window.clearTimeout(this.audioFirstChunkGuard)
        this.audioFirstChunkGuard = null
      }
      if (this.reconciliationTimeout != null) {
        window.clearTimeout(this.reconciliationTimeout)
        this.reconciliationTimeout = null
      }
      debugLog('[PublicAgentChat] onAudioStop', { chunks: this.audioChunkCount })
      this.streamBufferUtil?.flush()

      // Only finalize the player if we actually received audio chunks
      // If no chunks, the fallback TTS will load audio separately
      if (this.audioChunkCount > 0) {
        try { player?.endStream() } catch {
          // do nothing
        }
      }

      this.audioStreaming = false
      this.stopStreamAutoFollow()
      this.isStreaming = false
      const finalAssistantIndex = this.streamingMessageIndex
      this.streamingMessageIndex = null
      this.resetPtcTranscriptSuppression()
      this.hideToolActivity()
      this.loading = false
      const shouldKeepReplayState =
        finalAssistantIndex !== null && this.markKnownRetryableCompletion(finalAssistantIndex, 'voice')
      this.updateSession()
      this.scrollToBottom()
      // Clear resumable run state after successful completion
      this.clearCurrentRunId()
      if (!shouldKeepReplayState) {
        this.clearCurrentTurnReplay()
      }

      nextTick(() => {
        window.dispatchEvent(new Event('resize'))
        setTimeout(() => this.$forceUpdate(), 150)
      })
    },
    onError: (message: string) => {
      if (this.streamCancelledByUser) {
        this.finalizeLocalCancellation()
        return
      }
      console.error('[PublicAgentChat] onError', { message })
      // Cancel any pending fallbacks and reconciliation
      if (this.audioStartWatchTimer != null) {
        window.clearTimeout(this.audioStartWatchTimer)
        this.audioStartWatchTimer = null
      }
      if (this.audioFirstChunkGuard != null) {
        window.clearTimeout(this.audioFirstChunkGuard)
        this.audioFirstChunkGuard = null
      }
      if (this.reconciliationTimeout != null) {
        window.clearTimeout(this.reconciliationTimeout)
        this.reconciliationTimeout = null
      }
      if (!this.audioEnded) {
        try { player?.endStream() } catch {
          // do nothing
        }
        this.audioEnded = true
      }
      this.audioStreaming = false

      this.streamBufferUtil?.flush()
      const errorText = `Audio error: ${message}`
      const failedIndex = this.streamingMessageIndex
      if (this.streamingMessageIndex !== null) {
        const msg = this.localMessages[this.streamingMessageIndex]
        msg.content += `\n\n${errorText}`
      } else {
        this.localMessages.push({ role: 'assistant', content: errorText })
      }
      const failureKind = this.classifyTurnFailure(errorText, 'voice')
      const autoRetryCandidate = failedIndex !== null && this.isSafeAutoRetryCandidate('voice')
      if (failedIndex !== null) {
        this.applyFailedAssistantState(failedIndex, {
          failed: true,
          retryable: autoRetryCandidate,
          manualRetryAvailable: true,
          autoRetryAttempted: this.currentTurnReplay?.autoRetryAttempted === true,
          autoRetryPending: autoRetryCandidate,
          failureKind,
          errorMessage: errorText
        })
      }
      if (!autoRetryCandidate) {
        this.$store.dispatch('updateSnackbar', { text: errorText, color: 'error', timeout: 4000 })
      }
      this.stopStreamAutoFollow()
      this.isStreaming = false
      this.streamingMessageIndex = null
      this.resetPtcTranscriptSuppression()
      this.hideToolActivity()
      this.loading = false
      this.updateSession()
      if (autoRetryCandidate && failedIndex !== null) {
        void this.retryFailedAssistantResponse(failedIndex, 'auto')
      }
    },
    onCancel: () => {
      this.finalizeLocalCancellation()
    },
    onStatus: (info: StatusEvent) => {
      debugLog('[PublicAgentChat] onStatus', info)
      // When server signals audio stage, proactively try to start playback.
      if (info.stage === 'audio') {
        // Telemetry only. Playback is gated on first audio chunk or fallback synthesis after text_done.
        return
      }
    },
    onRun: (runId: string) => {
      // Persist the server-issued run id for resumable UX
      this.saveCurrentRunId(runId)
      debugLog('[PublicAgentChat] onRun', { runId })
    },
    onUsage: (usage: { promptTokens: number; completionTokens: number; totalTokens: number }) => {
      debugLog('[PublicAgentChat] onUsage', usage)
      if (this.streamingMessageIndex !== null) {
        const idx = this.streamingMessageIndex
        const updated = { ...this.localMessages[idx] }
        updated.tokenUsage = {
          promptTokens: usage.promptTokens,
          completionTokens: usage.completionTokens,
          totalTokens: usage.totalTokens
        }
        this.localMessages.splice(idx, 1, updated)
      }
    }
  } as const

  try {
    // Exclude the placeholder assistant message
    debugLog('[PublicAgentChat] calling streamingPublicAgentSpeak')
    this.activeStreamHandle = startStreamingPublicAgentSpeak(username, slug, this.session.id, this.localMessages.slice(0, -1), callbacks)
    await this.activeStreamHandle.promise
    debugLog('[PublicAgentChat] streamingPublicAgentSpeak completed')
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown streaming error'
    if (!this.streamCancelledByUser) {
      callbacks.onError(msg)
    }
  } finally {
    // Emergency cleanup: ensure UI state is finalized even if stream didn't properly close
    debugLog('[PublicAgentChat] streamChat finally block (voice)', {
      hasBuffer: false,
      isStreaming: this.isStreaming,
      loading: this.loading
    })

    // Flush any remaining buffered text
    this.streamBufferUtil?.flush()
    
    // Clear any pending timers
    if (this.audioStartWatchTimer != null) {
      window.clearTimeout(this.audioStartWatchTimer)
      this.audioStartWatchTimer = null
    }
    if (this.audioFirstChunkGuard != null) {
      window.clearTimeout(this.audioFirstChunkGuard)
      this.audioFirstChunkGuard = null
    }
    if (this.reconciliationTimeout != null) {
      window.clearTimeout(this.reconciliationTimeout)
      this.reconciliationTimeout = null
    }
    
    // Ensure streaming state is fully cleared
    if (this.isStreaming || this.loading) {
      this.audioStreaming = false
      this.stopStreamAutoFollow()
      this.isStreaming = false
      this.streamingMessageIndex = null
      this.resetPtcTranscriptSuppression()
      this.loading = false
      this.updateSession()
      this.scrollToBottom()
      
      // Trigger resize and force update to ensure UI reflects final state
      nextTick(() => {
        window.dispatchEvent(new Event('resize'))
        setTimeout(() => this.$forceUpdate(), 150)
      })
    }
    this.activeStreamHandle = null
    this.streamCancelledByUser = false
  }
  } else {
    // Text-only path for non-voice agents
    debugLog('[PublicAgentChat] Using text-only endpoint')
    this.isStreaming = true
    this.startStreamAutoFollow()
    
    const textCallbacks = {
      onChunk: (chunk: string) => {
        debugLog('[PublicAgentChat] onTextDelta (text-only)', { len: chunk.length, streamingIndex: this.streamingMessageIndex })
        this.logScrollSnapshot('stream.text.onChunk', {
          chunkLen: chunk.length,
          streamBufferLen: 0
        })
        const visibleChunk = this.filterAssistantTranscriptChunk(chunk)
        if (visibleChunk.length > 0) {
          this.markCurrentTurnProgress({ visibleAssistantDelta: true })
          this.handleResponseStreamingStarted()
        }
        if (this.streamingMessageIndex !== null) {
          this.streamBufferUtil?.append(visibleChunk)
        }
      },
      onComplete: (finalContent: string, usage?: TokenUsage, finishReason?: string | null) => {
        debugLog('[PublicAgentChat] onComplete (text-only)', { finishReason })
        this.logScrollSnapshot('stream.text.onComplete', {
          finalContentLen: finalContent.length,
          pendingBufferLen: 0,
          hasUsage: !!usage,
          finishReason: finishReason ?? null
        }, true)

        // Flush any remaining buffer
        this.streamBufferUtil?.flush()
        
        // Update with usage if provided
        if (usage && this.streamingMessageIndex !== null) {
          const idx = this.streamingMessageIndex
          const updated = { ...this.localMessages[idx] }
          updated.tokenUsage = {
            promptTokens: usage.promptTokens,
            completionTokens: usage.completionTokens,
            totalTokens: usage.totalTokens
          }
          this.localMessages.splice(idx, 1, updated)
        }

        const finalAssistantIndex = this.applyPendingContextMessagesToStreamingMessage()
        this.applyRefusalFinishReason(finalAssistantIndex, finishReason)
        const shouldKeepReplayState =
          finalAssistantIndex !== null && this.markKnownRetryableCompletion(finalAssistantIndex, 'text')

        this.isStreaming = false
        this.stopStreamAutoFollow()
        this.streamingMessageIndex = null
        this.resetPtcTranscriptSuppression()
        this.hideToolActivity()
        this.loading = false
        this.updateSession()
        this.scrollToBottom()
        // Clear resumable run state after successful completion
        this.clearCurrentRunId()
        if (!shouldKeepReplayState) {
          this.clearCurrentTurnReplay()
        }
        
        nextTick(() => {
          window.dispatchEvent(new Event('resize'))
          setTimeout(() => this.$forceUpdate(), 150)
        })
      },
      onError: (error: Error) => {
        if (this.streamCancelledByUser) {
          this.finalizeLocalCancellation()
          return
        }
        console.error('[PublicAgentChat] onError (text-only)', { message: error.message })
        
        // Flush any pending buffer
        this.streamBufferUtil?.flush()

        const errorText = `Error: ${error.message}`
        const failedIndex = this.streamingMessageIndex
        if (this.streamingMessageIndex !== null) {
          const msg = this.localMessages[this.streamingMessageIndex]
          msg.content += `\n\n${errorText}`
        } else {
          this.localMessages.push({ role: 'assistant', content: errorText })
        }
        const failureKind = this.classifyTurnFailure(errorText, 'text')
        const autoRetryCandidate = failedIndex !== null && this.isSafeAutoRetryCandidate('text')
        if (failedIndex !== null) {
          this.applyFailedAssistantState(failedIndex, {
            failed: true,
            retryable: autoRetryCandidate,
            manualRetryAvailable: true,
            autoRetryAttempted: this.currentTurnReplay?.autoRetryAttempted === true,
            autoRetryPending: autoRetryCandidate,
            failureKind,
            errorMessage: errorText
          })
        }
        if (!autoRetryCandidate) {
          this.$store.dispatch('updateSnackbar', { text: errorText, color: 'error', timeout: 4000 })
        }
        this.stopStreamAutoFollow()
        this.isStreaming = false
        this.streamingMessageIndex = null
        this.resetPtcTranscriptSuppression()
        this.hideToolActivity()
        this.loading = false
        this.pendingContextMessages = null
        this.updateSession()
        if (autoRetryCandidate && failedIndex !== null) {
          void this.retryFailedAssistantResponse(failedIndex, 'auto')
        }
      },
      onCancel: () => {
        this.finalizeLocalCancellation()
      },
      onMeta: (meta: { runId?: string; contextMessages?: ModelMessage[]; providerState?: GenerateResultProviderState }) => {
        debugLog('[PublicAgentChat] onMeta (text-only)', meta)
        if (meta.runId) {
          this.saveCurrentRunId(meta.runId)
        }
        if (Array.isArray(meta.contextMessages) && meta.contextMessages.length > 0) {
          this.pendingContextMessages = meta.contextMessages
        }
        if (meta.providerState) {
          this.sessionProviderState = this.sanitizeSessionProviderState(meta.providerState)
        }
      },
      onReset: () => {
        // The server discarded this assistant turn and is retrying it (e.g. Anthropic PTC
        // empty-boundary recovery). Restore the in-progress message to a clean slate so the retry
        // streams a fresh response into the same slot, mirroring the initial assistant-message setup.
        debugLog('[PublicAgentChat] onReset (text-only)')
        this.streamBufferUtil?.reset()
        if (this.streamingMessageIndex !== null) {
          const idx = this.streamingMessageIndex
          this.localMessages.splice(idx, 1, { role: 'assistant', content: '' })
          this.beginToolEventRouting(idx)
        }
        this.resetPtcTranscriptSuppression()
        this.hideToolActivity()
        this.resetCurrentTurnProgress()
        this.pendingContextMessages = null
      }
    }

    try {
      debugLog('[PublicAgentChat] calling streamingChatWithPublicAgent')
      this.activeStreamHandle = startStreamingChatWithPublicAgent(username, slug, this.session.id, this.localMessages.slice(0, -1), textCallbacks)
      await this.activeStreamHandle.promise
      debugLog('[PublicAgentChat] streamingChatWithPublicAgent completed')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown streaming error'
      if (!this.streamCancelledByUser) {
        textCallbacks.onError(new Error(msg))
      }
    } finally {
      debugLog('[PublicAgentChat] streamChat finally block (text-only)', {
        hasBuffer: false,
        isStreaming: this.isStreaming,
        loading: this.loading
      })

      // Flush any remaining buffered text
      this.streamBufferUtil?.flush()
      
      // Ensure streaming state is fully cleared
      if (this.isStreaming || this.loading) {
        this.stopStreamAutoFollow()
        this.isStreaming = false
        this.streamingMessageIndex = null
        this.resetPtcTranscriptSuppression()
        this.loading = false
        this.updateSession()
        this.scrollToBottom()
        
        nextTick(() => {
          window.dispatchEvent(new Event('resize'))
          setTimeout(() => this.$forceUpdate(), 150)
        })
      }
      this.activeStreamHandle = null
      this.streamCancelledByUser = false
      this.pendingContextMessages = null
    }
  }
}

  // ---- Resumable Run Helpers ----
  private reconcileWithRunSnapshot(): void {
    const runId = this.currentRunId
    if (!runId || this.streamingMessageIndex == null) return

    let unsub: (() => void) | null = null
    let timeout: number | null = null

    const cleanup = () => {
      try { unsub && unsub() } catch { /* ignore */ }
      unsub = null
      if (timeout != null) {
        try { window.clearTimeout(timeout) } catch { /* ignore */ }
        timeout = null
      }
    }

    try {
      unsub = subscribePublicRun(runId, {
        onSnapshot: (snap) => {
          try {
            const idx = this.streamingMessageIndex
            if (idx == null) return
            const current = this.localMessages[idx]
            const currLen = typeof current?.content === 'string' ? current.content.length : 0
            const rawSnapshot = typeof snap.content === 'string' ? snap.content : ''
            const sanitizedSnapshot = this.sanitizeAssistantTranscriptSnapshot(rawSnapshot)
            const srv = sanitizedSnapshot.content
            if (srv && srv.length >= currLen) {
              this.ptcFilter.isActive = sanitizedSnapshot.suppressing
              if (srv.length > currLen) {
                this.handleResponseStreamingStarted()
              }
              const updated = { ...current, content: srv }
              this.localMessages.splice(idx, 1, updated)
              this.scheduleScroll()
            }
          } finally {
            cleanup()
          }
        },
        onTextDelta: (delta) => {
          // Best-effort: if server emits tail deltas before snapshot, append them.
          try {
            const visibleDelta = typeof delta === 'string' ? this.filterAssistantTranscriptChunk(delta) : ''
            if (this.streamingMessageIndex != null && visibleDelta.length > 0) {
              this.handleResponseStreamingStarted()
              const idx = this.streamingMessageIndex
              const updated = { ...this.localMessages[idx] }
              updated.content += visibleDelta
              this.localMessages.splice(idx, 1, updated)
              this.scheduleScroll()
            }
          } catch { /* ignore */ }
        },
        onDone: () => cleanup(),
        onError: () => cleanup()
      })

      // Safety timeout: auto-cleanup if no snapshot arrives promptly
      timeout = window.setTimeout(() => cleanup(), 500)
    } catch {
      cleanup()
    }
  }

  private getRunStorageKey(): string {
    // Namespace resume key by owner/agent to avoid collisions
    return `msq.public.run.${this.ownerUsername}/${this.agentSlug}`
  }

  private saveCurrentRunId(runId: string) {
    this.currentRunId = runId
    try { sessionStorage.setItem(this.getRunStorageKey(), runId) } catch { /* ignore */ }
  }

  private clearCurrentRunId() {
    // Stop any active run subscription
    try { this.runStreamUnsubscribe?.() } catch { /* ignore */ }
    this.runStreamUnsubscribe = null
    this.currentRunId = null
    try { sessionStorage.removeItem(this.getRunStorageKey()) } catch { /* ignore */ }
  }

  private tryAutoResumeRun() {
    if (this.isStreaming) return
    let stored: string | null = null
    try { stored = sessionStorage.getItem(this.getRunStorageKey()) } catch { /* ignore */ }
    if (!stored || stored.trim().length === 0) return
    // Create/identify the assistant message to resume into
    if (this.streamingMessageIndex == null) {
      const last = this.localMessages[this.localMessages.length - 1]
      if (!last || last.role !== 'assistant') {
        this.localMessages.push({ role: 'assistant', content: '' })
      }
      this.streamingMessageIndex = this.localMessages.length - 1
    }
    // Subscribe to run tail
    const runId = stored
    this.currentRunId = runId
    this.runStreamUnsubscribe = subscribePublicRun(runId, {
      onSnapshot: (snap) => {
        // Seed with server snapshot content
        if (this.streamingMessageIndex != null) {
          const idx = this.streamingMessageIndex
          const updated = { ...this.localMessages[idx] }
          const sanitizedSnapshot = this.sanitizeAssistantTranscriptSnapshot(snap.content || '')
          this.ptcFilter.isActive = sanitizedSnapshot.suppressing
          if (sanitizedSnapshot.content.length > 0) {
            this.handleResponseStreamingStarted()
          }
          updated.content = sanitizedSnapshot.content
          this.localMessages.splice(idx, 1, updated)
        }
      },
      onTextDelta: (delta) => {
        const visibleDelta = typeof delta === 'string' ? this.filterAssistantTranscriptChunk(delta) : ''
        if (this.streamingMessageIndex != null && visibleDelta.length > 0) {
          this.handleResponseStreamingStarted()
          const idx = this.streamingMessageIndex
          const updated = { ...this.localMessages[idx] }
          updated.content += visibleDelta
          this.localMessages.splice(idx, 1, updated)
          this.scheduleScroll()
        }
      },
      onDone: () => {
        // End of run; clear and finalize state
        this.stopStreamAutoFollow()
        this.streamingMessageIndex = null
        this.resetPtcTranscriptSuppression()
        this.hideToolActivity()
        this.clearCurrentRunId()
        this.isStreaming = false
        this.loading = false
        this.updateSession()
      },
      onError: (msg) => {
        console.warn('[PublicAgentChat] resume run stream error', msg)
        this.resetPtcTranscriptSuppression()
        this.hideToolActivity()
        this.clearCurrentRunId()
      }
    })
    // Reflect state in UI
    this.isStreaming = true
    this.loading = true
    this.startStreamAutoFollow()
  }

  updateSession() {
    if (isChatEmpty(this.localMessages)) {
      return
    }
    const updatedSession = {
      ...this.session,
      messages: [...this.localMessages],
      ...(this.sessionProviderState ? { providerState: this.sessionProviderState } : {}),
      updatedAt: Date.now(),
    };
    this.savePublicChat(updatedSession);
    if (!this.loading && !this.isStreaming) {
      void this.persistPendingGeneratedTitle()
    }
  }

  private async persistPendingGeneratedTitle(): Promise<void> {
    if (!this.pendingGeneratedTitle || this.persistingGeneratedTitle) {
      return
    }

    const titleToPersist = this.pendingGeneratedTitle
    this.persistingGeneratedTitle = true

    try {
      const updatedSession = {
        ...this.session,
        messages: [...this.localMessages],
        ...(this.sessionProviderState ? { providerState: this.sessionProviderState } : {}),
        title: titleToPersist,
        updatedAt: Date.now(),
      }

      const persisted = await this.updatePublicChatTitle(updatedSession)
      if (persisted) {
        this.session.title = titleToPersist
        this.pendingGeneratedTitle = null
      }
    } finally {
      this.persistingGeneratedTitle = false
    }
  }

  private resolveScrollDebugEnabled(): boolean {
    if (typeof window === 'undefined') return false

    let storageValue: string | null = null
    try {
      storageValue = window.localStorage.getItem(this.scrollDebugStorageKey)
    } catch {
      storageValue = null
    }

    const params = new URLSearchParams(window.location.search)
    const queryValue = params.get('scrollDebug')
    const globalValue = (window as unknown as { __MSQ_SCROLL_DEBUG__?: unknown }).__MSQ_SCROLL_DEBUG__

    return this.scrollDebug === true
      || queryValue === '1'
      || queryValue === 'true'
      || storageValue === '1'
      || storageValue === 'true'
      || globalValue === true
  }

  private getElementMetrics(el: HTMLElement | null): Record<string, unknown> | null {
    if (!el) return null
    const rect = el.getBoundingClientRect()
    const style = window.getComputedStyle(el)
    const className = typeof el.className === 'string' ? el.className : ''
    const shortClassName = className.length > 120 ? `${className.slice(0, 117)}...` : className
    return {
      tag: el.tagName.toLowerCase(),
      className: shortClassName,
      rect: {
        top: Math.round(rect.top),
        bottom: Math.round(rect.bottom),
        left: Math.round(rect.left),
        right: Math.round(rect.right),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      },
      scrollTop: Math.round(el.scrollTop),
      scrollHeight: Math.round(el.scrollHeight),
      clientHeight: Math.round(el.clientHeight),
      clientWidth: Math.round(el.clientWidth),
      offsetHeight: Math.round(el.offsetHeight),
      overflowY: style.overflowY
    }
  }

  private findNearestScrollableContainer(targetEl: HTMLElement): HTMLElement | null {
    let container: HTMLElement | null = targetEl.parentElement
    while (container) {
      const style = window.getComputedStyle(container)
      const canScroll = (style.overflowY === 'auto' || style.overflowY === 'scroll')
        && container.scrollHeight > container.clientHeight
      if (canScroll) {
        return container
      }
      container = container.parentElement
    }
    return null
  }

  private logScrollSnapshot(reason: string, extra: Record<string, unknown> = {}, force = false): void {
    if (!this.scrollDebugEnabled || typeof window === 'undefined') return

    const now = Date.now()
    if (!force && now - this.scrollDebugLastLogAt < this.scrollDebugThrottleMs) {
      return
    }
    this.scrollDebugLastLogAt = now

    const rootEl = this.$el instanceof HTMLElement ? this.$el : null
    const chatArea = this.resolveChatAreaElement()
    const chatInput = this.resolveChatInputElement()
    const lastMessage = this.resolveLastMessageElement()
    const chatContentArea = rootEl?.closest('.chat-content-area') as HTMLElement | null
    const dashboardContainer = rootEl?.closest('.dashboard-chat-container') as HTMLElement | null
    const nearestInputScrollContainer = chatInput ? this.findNearestScrollableContainer(chatInput) : null
    const inputRect = chatInput?.getBoundingClientRect()
    const viewportBottom = window.innerHeight - this.scrollDebugMinBottomInsetPx
    const hiddenBottomPx = inputRect ? Math.max(0, Math.ceil(inputRect.bottom - viewportBottom)) : null

    const docEl = document.documentElement
    const body = document.body

    debugLog('[CHAT_SCROLL_DEBUG]', {
      reason,
      ts: now,
      state: {
        isStreaming: this.isStreaming,
        loading: this.loading,
        streamingMessageIndex: this.streamingMessageIndex,
        messageCount: this.localMessages.length
      },
      viewport: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        scrollY: Math.round(window.scrollY),
        scrollX: Math.round(window.scrollX)
      },
      document: {
        clientHeight: docEl.clientHeight,
        scrollHeight: docEl.scrollHeight,
        bodyClientHeight: body?.clientHeight ?? null,
        bodyScrollHeight: body?.scrollHeight ?? null
      },
      inputVisibility: {
        hiddenBottomPx,
        minBottomInsetPx: this.scrollDebugMinBottomInsetPx
      },
      elements: {
        root: this.getElementMetrics(rootEl),
        dashboardContainer: this.getElementMetrics(dashboardContainer),
        chatContentArea: this.getElementMetrics(chatContentArea),
        chatArea: this.getElementMetrics(chatArea),
        chatInput: this.getElementMetrics(chatInput),
        lastMessage: this.getElementMetrics(lastMessage),
        nearestInputScrollContainer: this.getElementMetrics(nearestInputScrollContainer)
      },
      extra
    })
  }

  private resolveChatAreaElement(): HTMLElement | null {
    const chatAreaRef = (this.$refs as { chatArea?: unknown } | undefined)?.chatArea
    if (chatAreaRef instanceof HTMLElement) {
      return chatAreaRef
    }

    if (chatAreaRef && typeof chatAreaRef === 'object' && '$el' in chatAreaRef) {
      const el = (chatAreaRef as { $el?: unknown }).$el
      if (el instanceof HTMLElement) {
        return el
      }
    }

    if (this.$el instanceof HTMLElement) {
      const queryResult = this.$el.querySelector('.chat-area')
      if (queryResult instanceof HTMLElement) {
        return queryResult
      }
    }

    return null
  }

  private resolveChatInputElement(): HTMLElement | null {
    const inputRef = (this.$refs as { chatInputArea?: unknown } | undefined)?.chatInputArea
    if (inputRef instanceof HTMLElement) {
      return inputRef
    }

    if (inputRef && typeof inputRef === 'object' && '$el' in inputRef) {
      const el = (inputRef as { $el?: unknown }).$el
      if (el instanceof HTMLElement) {
        return el
      }
    }

    return null
  }

  private resolveLastMessageElement(): HTMLElement | null {
    const last = this.messageElements[this.messageElements.length - 1]
    if (!last) return null
    if (last instanceof HTMLElement) return last
    if (typeof last === 'object' && '$el' in last) {
      const el = (last as { $el?: unknown }).$el
      if (el instanceof HTMLElement) {
        return el
      }
    }
    return null
  }

  private scheduleViewportHeightSync(): void {
    if (typeof window === 'undefined') return
    if (this.viewportLayoutRafId != null) return
    this.viewportLayoutRafId = window.requestAnimationFrame(() => {
      this.viewportLayoutRafId = null
      this.syncViewportHeight()
    })
  }

  private syncViewportHeight(): void {
    if (typeof window === 'undefined') return
    const rootEl = this.$el instanceof HTMLElement ? this.$el : null
    if (!rootEl) return
    const top = Math.max(0, Math.round(rootEl.getBoundingClientRect().top))
    const availableHeight = Math.max(0, window.innerHeight - top)
    rootEl.style.setProperty('--chat-available-height', `${availableHeight}px`)
  }

  private resetAutoScrollState(): void {
    this.autoScrollPaused = false
  }

  private alignChatAreaToBottom(): void {
    const chatArea = this.resolveChatAreaElement()
    if (!chatArea) return
    chatArea.scrollTop = chatArea.scrollHeight
  }

  private startStreamAutoFollow(): void {
    if (typeof window === 'undefined') return
    this.autoScrollPaused = false
    this.scrollToBottom('streamAutoFollow.start')
  }

  private stopStreamAutoFollow(): void {
    if (typeof window === 'undefined') return
    if (this.scrollTimer != null) {
      window.clearTimeout(this.scrollTimer)
      this.scrollTimer = null
    }
  }

  resumeAutoScroll(): void {
    this.autoScrollPaused = false
    this.alignChatAreaToBottom()
    if (this.isStreaming) {
      this.startStreamAutoFollow()
    }
  }

  // Same constraint as scheduleScroll: a class-property arrow captures the ref-less harvest
  // instance and resolveChatAreaElement() returns null forever.
  scrollToBottom(source = 'scrollToBottom'): void {
    if (this.autoScrollPaused) return
    this.logScrollSnapshot(`${source}.requested`, {}, true)
    nextTick(() => {
      const chatArea = this.resolveChatAreaElement()

      if (!chatArea) {
        this.logScrollSnapshot(`${source}.missingChatArea`, {}, true)
        return
      }

      const beforeTop = chatArea.scrollTop
      window.requestAnimationFrame(() => {
        chatArea.scrollTop = chatArea.scrollHeight
      })

      this.logScrollSnapshot(`${source}.chatAreaScroll`, {
        scrollHeight: Math.round(chatArea.scrollHeight),
        clientHeight: Math.round(chatArea.clientHeight),
        beforeTop: Math.round(beforeTop),
        afterTop: Math.round(chatArea.scrollTop)
      }, true)
    })
  }

  shouldShowToolActivity(message: ModelMessage, index: number): boolean {
    return message.role === 'assistant'
      && this.toolActivityVisible
      && this.toolActivityMessageIndex === index
  }

  async generateChatTitle(userPrompt: string): Promise<void> {
    // Only generate title if:
    // 1. A title agent is configured (store or owner-provided via public details)
    // 2. This is the first message (current title is "New Chat")
    // 3. We're not already loading
    const selectedTitleAgent = this.titleAgent ?? this.agent?.settings?.titleAgentName ?? null;
    if (!selectedTitleAgent || this.session.title !== 'New Chat' || this.loading) {
      return;
    }
    // Guard: public viewers cannot use authenticated prompt endpoint
    if (!this.authenticated) {
      return;
    }

    try {
      // Create a specific prompt for title generation
      const titlePrompt = `Generate a concise, descriptive title (max 5 words) for a conversation that starts with: "${userPrompt}". Respond with only the title, no quotes or punctuation.`;
      
      const messages: ModelMessage[] = [{
        role: 'user',
        content: titlePrompt
      }];

      let responseContent: string | null = null
      const sharedRef = decodeSharedTitleAgent(selectedTitleAgent)
      if (sharedRef) {
        const response = await chatWithPublicAgent(sharedRef.ownerUsername, sharedRef.slug, messages, false)
        if (isApiError(response) || !response.content) {
          return
        }
        responseContent = response.content
      } else {
        // Call the title agent with a timeout to ensure it doesn't block
        const response = await this.$store.dispatch('promptAgent', {
          messages,
          name: selectedTitleAgent,
          timeout: 180_000
        });
        if (response.error || !response.content) {
          return
        }
        responseContent = response.content
      }

      if (responseContent) {
        // Extract and clean the title
        let generatedTitle = responseContent.trim();
        
        // Remove quotes if present
        generatedTitle = generatedTitle.replace(/^["']|["']$/g, '');
        
        // Limit length to 50 characters
        if (generatedTitle.length > 50) {
          generatedTitle = generatedTitle.substring(0, 47) + '...';
        }

        this.pendingGeneratedTitle = generatedTitle
        this.session.title = generatedTitle
        this.savePublicChat({
          ...this.session,
          messages: [...this.localMessages],
          ...(this.sessionProviderState ? { providerState: this.sessionProviderState } : {}),
          title: generatedTitle,
          updatedAt: Date.now(),
        })

        if (!this.loading && !this.isStreaming) {
          await this.persistPendingGeneratedTitle()
        }
      }
    } catch (error) {
      // Silently fail - title generation should not interrupt the chat
      console.error('Failed to generate chat title:', error);
    }
  }



  /**
  * Copy text to clipboard and show feedback
  */
  async copyToClipboard(text: string, successMessage: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.$store.dispatch('updateSnackbar', { 
        text: successMessage, 
        color: 'success',
        timeout: 3000 
      });
      
      // Track analytics
      this.trackEvent('public_chat_copy_action', {
        agent_id: this.agentHash,
        copy_type: successMessage
      });
    } catch (error) {
      console.error('Failed to copy:', error);
      this.$store.dispatch('updateSnackbar', { 
        text: 'Failed to copy to clipboard', 
        color: 'error',
        timeout: 3000 
      });
    }
  }

  /**
  * Copy individual message as markdown
  */
  async copyMessageMarkdown(message: ModelMessage): Promise<void> {
    const content = getMessageText(message);
    await this.copyToClipboard(content, 'Message copied as markdown');
  }

  /**
  * Copy individual message as plain text
  */
  async copyMessageText(message: ModelMessage): Promise<void> {
    const content = getMessageText(message);
    const plainText = stripMarkdown(content);
    await this.copyToClipboard(plainText, 'Message copied as text');
  }

  /**
  * Copy tool events for a message
  */
  async copyMessageToolEvents(message: ModelMessage): Promise<void> {
    const toolEvents = message.toolEvents || [];
    const json = JSON.stringify(toolEvents, null, 2);
    await this.copyToClipboard(json, 'Tool events copied');
  }

  /**
  * Copy message with both content and tool events
  */
  async copyMessageAll(message: ModelMessage): Promise<void> {
    const parts: string[] = [];
    
    // Add tool events if present
    if (message.toolEvents && message.toolEvents.length > 0) {
      parts.push('=== TOOL EVENTS ===');
      parts.push(JSON.stringify(message.toolEvents, null, 2));
      parts.push('');
    }
    
    // Add message content
    parts.push('=== MESSAGE ===');
    parts.push(getMessageText(message));
    
    const combined = parts.join('\n');
    await this.copyToClipboard(combined, 'Message and tool events copied');
  }

  /**
  * Copy all assistant responses as markdown
  */
  async copyAllResponsesMarkdown(): Promise<void> {
    const responses = this.localMessages
      .filter(msg => msg.role === 'assistant')
      .map(msg => getMessageText(msg))
      .join('\n\n---\n\n');

    await this.copyToClipboard(responses, 'All responses copied as markdown');
  }

  /**
  * Copy all assistant responses with tool events
  */
  async copyAllResponsesWithToolEvents(): Promise<void> {
    const messages = this.localMessages.filter(msg => msg.role === 'assistant');
    const formatted = messages.map((msg, index) => {
      const parts: string[] = [`=== RESPONSE ${index + 1} ===`];

      if (msg.toolEvents && msg.toolEvents.length > 0) {
        parts.push('\nTOOL EVENTS:');
        parts.push(JSON.stringify(msg.toolEvents, null, 2));
      }

      parts.push('\nCONTENT:');
      parts.push(getMessageText(msg));

      return parts.join('\n');
    }).join('\n\n---\n\n');

    await this.copyToClipboard(formatted, 'All responses and tool events copied');
  }

  /**
  * Copy all messages (user and assistant) with tool events
  */
  async copyAllMessagesWithToolEvents(): Promise<void> {
    const formatted = this.localMessages.map((msg, index) => {
      const parts: string[] = [`=== ${msg.role.toUpperCase()} MESSAGE ${index + 1} ===`];
      
      if (msg.toolEvents && msg.toolEvents.length > 0) {
        parts.push('\nTOOL EVENTS:');
        parts.push(JSON.stringify(msg.toolEvents, null, 2));
      }
      
      parts.push('\nCONTENT:');
      parts.push(getMessageText(msg));
      
      return parts.join('\n');
    }).join('\n\n---\n\n');
    
    await this.copyToClipboard(formatted, 'All messages and tool events copied');
  }

  /**
   * Check if a message contains financial chart data
   */
  hasFinancialChart(message: ModelMessage): boolean {
    if (!message.toolEvents || message.toolEvents.length === 0) {
      return false
    }
    
    return message.toolEvents.some(event => isFinancialToolEvent(event))
  }

  /**
   * Extract financial data from a message's tool events
   */
  getAllFinancialChartData(message: ModelMessage): Array<{ data: FinancialDataResponse; index: number }> {
    if (!message.toolEvents || message.toolEvents.length === 0) {
      return []
    }
    
    const allChartData: Array<{ data: FinancialDataResponse; index: number }> = []
    
    message.toolEvents.forEach((event, index) => {
      const data = extractFinancialData(event)
      if (data) {
        allChartData.push({ data, index })
      }
    })
    
    return allChartData
  }

  /**
   * Generate a descriptive title for a chart based on its metadata
   */
  getChartTitle(data: FinancialDataResponse): string {
    const symbol = getSymbolFromMetaData(data.meta_data)
    const interval = data.meta_data.interval || 'Daily'
    const lastRefreshed = data.meta_data.last_refreshed
    
    return `${symbol} - ${interval} (Last updated: ${new Date(lastRefreshed).toLocaleString()})`
  }

  /**
   * Get chart dimensions based on viewport
   */
  getChartDimensions(): { width: number; height: number } {
    // Get the chat area width for responsive sizing
    const chatArea = this.resolveChatAreaElement()
    
    // Try multiple fallback options for width
    let containerWidth = 0
    
    if (chatArea && chatArea.clientWidth > 0) {
      containerWidth = chatArea.clientWidth
    } else if (chatArea && chatArea.offsetWidth > 0) {
      containerWidth = chatArea.offsetWidth
    } else if (chatArea && chatArea.parentElement) {
      containerWidth = chatArea.parentElement.clientWidth
    } else {
      // Use viewport width as last resort
      containerWidth = window.innerWidth - 100
    }
    
    const maxWidth = Math.max(300, containerWidth - 48) // Ensure minimum width
    const width = Math.min(maxWidth, 800)
    const height = Math.min(width * 0.6, 500) // Maintain aspect ratio
    
    return { width, height }
  }

  /**
  * Email a single message
  */
  async emailMessage(message: ModelMessage): Promise<void> {
    const messageContent = getMessageText(message)
    const role = message.role === 'user' ? 'User' : this.agent.name
    const conversationTitle = this.session.title || 'New Chat'
    
    const emailData: SendResultsEmailRequest = {
      headline: `${this.agent.name} - ${conversationTitle}`,
      body: `Message from ${role}:\n\n${messageContent}`
    }

    await this.sendEmailRequest(emailData, 'Message')
  }

  /**
  * Email all messages in the conversation
  */
  async emailAllMessages(): Promise<void> {
    const conversationTitle = this.session.title || 'New Chat'
    const formattedMessages = this.localMessages.map((msg, index) => {
      const role = msg.role === 'user' ? (this.username || 'User') : this.agent.name
      const content = getMessageText(msg)
      return `[${role}]:\n${content}`
    }).join('\n\n---\n\n')

    const emailData: SendResultsEmailRequest = {
      headline: `${this.agent.name} - ${conversationTitle}`,
      body: `Full conversation with ${this.agent.name}:\n\n${formattedMessages}`
    }

    await this.sendEmailRequest(emailData, 'Conversation')
  }

  /**
  * Send the email request with error handling
  */
  async sendEmailRequest(data: SendResultsEmailRequest, type: string): Promise<void> {
    this.loading = true
    
    try {
      const response = await sendResultsEmail(data)
      
      if (isApiError(response)) {
        throw new Error(response.message)
      }

      if (response.success) {
        this.$store.dispatch('updateSnackbar', { 
          text: `${type} emailed successfully!`, 
          color: 'success',
          timeout: 3000 
        })
        
        // Track analytics
        this.trackEvent('public_chat_email_sent', {
          agent_id: this.agentHash,
          email_type: type.toLowerCase(),
          message_count: type === 'Conversation' ? this.localMessages.length : 1
        })
      } else {
        throw new Error(response.message || 'Failed to send email')
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to send email'
      
      this.$store.dispatch('updateSnackbar', { 
        text: errorMessage, 
        color: 'error',
        timeout: 5000 
      })
      
      // Track error
      this.trackEvent('public_chat_email_error', {
        agent_id: this.agentHash,
        email_type: type.toLowerCase(),
        error_message: errorMessage
      })
    } finally {
      this.loading = false
    }
  }

  // Generate-and-play fallback for current streaming message when no chunks arrive
  private async fallbackGenerateAndPlayCurrent(index: number): Promise<void> {
    if (index < 0 || index >= this.localMessages.length) return
    const msg = this.localMessages[index]
    if (msg.role !== 'assistant') return
    const text = getMessageText(msg)
    if (!text || text.trim().length === 0) return

    try {
      const payload = {
        text,
        responseFormat: 'opus' as const
      }
      const res = await (this.$store.dispatch as any)('synthesizeSpeechForText', payload)
      if (!res || (res && 'error' in res)) {
        throw new Error((res && res.message) || 'Fallback TTS unavailable')
      }

      const bytes = new Uint8Array(res.buffer)
      const mime = res.contentType || 'audio/webm; codecs=opus'
      // Play immediately on the per-message player
      await nextTick()
      const player = this.streamingPlayers[index]
      player?.loadBytes(bytes, mime, true)
    } catch (e) {
      console.error('[PublicAgentChat] fallbackGenerateAndPlayCurrent failed', e)
      this.$store.dispatch('updateSnackbar', { text: 'Audio generation failed.', color: 'error', timeout: 3000 })
    }
  }

  // Download cached audio for a given assistant message index
  async downloadAudio(index: number): Promise<void> {
    if (index < 0 || index >= this.localMessages.length) return
    const msg = this.localMessages[index]
    if (msg.role !== 'assistant') return
    const text = getMessageText(msg)
    if (!text || text.trim().length === 0) {
      this.$store.dispatch('updateSnackbar', { text: 'No audio available for this message.', color: 'info' }, { root: true } as any)
      return
    }
    try {
      const res = await (this.$store.dispatch as any)('synthesizeSpeechForText', {
        text,
        responseFormat: 'opus' as const
      })
      if (!res || (res && 'error' in res)) {
        throw new Error((res && res.message) || 'TTS unavailable')
      }
      const bytes = new Uint8Array(res.buffer)
      const mime = res.contentType || 'audio/webm; codecs=opus'
      const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
      const blob = new Blob([ab], { type: mime })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      const tsStr = `${ts.getFullYear()}${pad(ts.getMonth() + 1)}${pad(ts.getDate())}-${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}`
      const defaultName = `${this.agent.name || 'agent'}_${index + 1}_${tsStr}.ogg`
      a.download = defaultName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      setTimeout(() => { try { URL.revokeObjectURL(url) } catch { /* ignore */ } }, 2000)
    } catch (e) {
      this.$store.dispatch('updateSnackbar', { text: 'Failed to download audio.', color: 'error' }, { root: true } as any)
    }
  }

  // Regenerate audio for a given assistant message index
  async regenerateAudio(index: number): Promise<void> {
    if (index < 0 || index >= this.localMessages.length) return
    const msg = this.localMessages[index]
    if (msg.role !== 'assistant') return
    const text = getMessageText(msg)

    // Attempt non-streaming TTS via store (enforces payload contract: voiceName OR providerKey+voice)
    // If no voice alias/default is available in FE, fallback to streaming public speak.
    try {
      const payload = {
        text,
        // If you wire a saved voice alias for public agent, set it here:
        // voiceName: this.agent?.voiceName ?? undefined,
        responseFormat: 'opus' as const
      }
      const res = await (this.$store.dispatch as any)('synthesizeSpeechForText', payload)
      if (!res || (res && 'error' in res)) {
        throw new Error((res && res.message) || 'TTS unavailable for this viewer (missing voice)')
      }

      const bytes = new Uint8Array(res.buffer)
      const mime = res.contentType || 'audio/webm; codecs=opus'

      await nextTick()
      const player = this.streamingPlayers[index]
      player?.loadBytes(bytes, mime, true)

      this.$store.dispatch('updateSnackbar', { text: 'Audio regenerated.', color: 'success' }, { root: true } as any)
      return
    } catch (_ttsErr) {
      this.$store.dispatch('updateSnackbar', { text: 'Failed to regenerate audio (TTS unavailable).', color: 'error' }, { root: true })
    }

  }
}
</script>

<style lang="scss" scoped>
.chat-wrapper.v-container {
  align-items: stretch;
  justify-content: flex-start;
  flex-wrap: nowrap;
  height: var(--chat-available-height, 100%);
  max-height: var(--chat-available-height, 100%);
  min-height: 0;
  width: 100%;
  overflow: hidden;
  align-items: stretch !important;
  justify-content: flex-start !important;
  flex-wrap: nowrap !important;
}

.chat-card {
  position: relative;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.scroll-to-bottom-fab {
  position: absolute;
  bottom: 80px;
  right: 24px;
  z-index: 10;
  opacity: 0.9;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 1;
  }
}

.chat-input-area {
  flex: 0 0 auto;
}
.chat-input {
  :deep(.v-field__prepend-inner),
  :deep(.v-field__append-inner) {
    align-self: end;
    padding-bottom: 8px;
  }
  :deep(.v-field__append-inner) {
    padding-bottom: 11px;
  }
}

.stream-stop-wrapper {
  position: relative;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stream-stop-btn {
  position: absolute;
  inset: 0;
  margin: auto;
  min-width: 20px !important;
  width: 20px !important;
  height: 20px !important;
}


.chat-area {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  display: block;
  // padding: 16px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* Internet Explorer 10+ */
}

.chat-area::-webkit-scrollbar { /* WebKit */
  width: 0;
  height: 0;
}



:deep(pre) {
  white-space: pre-wrap !important;
  word-wrap: break-word !important;
}

.message-name {
  font-weight: 500;
  opacity: 0.8;
}

.message-container {
  position: relative;
  border-radius: 8px;
  padding: 12px;
  
  &.user-message {
    background-color: rgb(var(--v-theme-surface-variant));
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  }
  
  &.assistant-message {
    border: 1px solid rgba(var(--v-theme-primary), 0.3);
    background-color: transparent;
    /* Reserve space for bottom-left absolute buttons so content (e.g., player) doesn't overlap */
    padding-bottom: 56px;
  }
}

// Per-message copy menu positioning
.message-copy-menu {
  position: absolute;
  bottom: 8px;
  left: 8px;
  
  .v-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
}

// Position email button for messages
.message-email-button {
  position: absolute;
  bottom: 8px;
  left: 42px;
  
  .v-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;
    
    &:hover {
      opacity: 1;
    }
  }
}

.message-download-button {
  position: absolute;
  bottom: 8px;
  left: 72px;
  .v-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;
    &:hover { opacity: 1; }
  }
}

.message-regenerate-button {
  position: absolute;
  bottom: 8px;
  left: 102px;
  .v-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;
    &:hover { opacity: 1; }
  }
}

.message-retry-button {
  position: absolute;
  bottom: 8px;
  left: 132px;
  .v-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;
    &:hover { opacity: 1; }
  }
}

.message-generating {
  position: absolute;
  bottom: 8px;
  right: 8px;
}

// Show email button on hover
.message-container:hover .message-email-button .v-btn {
  opacity: 1;
}

// Adjust message-copy-menu position to accommodate email button
.message-copy-menu {
  left: 8px;
  right: auto; // Remove right positioning
}

// Show menu button on hover
.message-container:hover .message-copy-menu .v-btn {
  opacity: 1;
}

// Full chat copy menu positioning
.chat-copy-menu {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-right: 16px;
  
  .v-btn {
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
    }
  }
}

// Ensure menus appear above other content
.v-menu {
  z-index: 2000;
}

// Add width constraint for tool events

.token-usage-display {
  padding: 4px 12px;
  font-size: 12px;
  color: rgba(var(--v-theme-primary), 0.8);
  text-align: right;
  
  .token-usage-label {
    font-weight: 500;
  }
  
  .token-usage-value {
    font-family: monospace;
  }
}

/* Per-response player spacing within assistant messages */
.assistant-message .message-player {
  margin-top: 8px;
  margin-bottom: 16px;
}

/* Refusal notice sits inline under the response, clear of the reserved action-button strip */
.assistant-message .refusal-notice {
  margin-bottom: 4px;
}

.headline {
  min-width: 0; // allow flex children to shrink below content size
}

.chat-header-agent-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.chat-header-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.chat-header-description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex-shrink: 1;
}

</style>
